import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import Swal from 'sweetalert2'
import CustomSelect from '../../modules/custom-select/CustomSelect'
import type {
  ExamQuestionAssignment,
  ExamQuestionAssignmentGroup,
} from '../my-course-exam/manual/core/_models'
import {getExamAssignments as fetchExamAssignments} from '../my-course-exam/manual/core/_requests'
import {
  OpticalFormExamResult,
  OpticalFormFinalizePayload,
  OpticalFormStudentEntry,
  OpticalStudentEntryStatus,
  OpticalFormFinalizeQuestionAdjustment,
  OpticalFormFinalizeExamOptions,
  OpticalFormExamSummary,
  OpticalFormQuestion,
  OpticalFormExamStudent,
  OpticalFormQuestionAnalysis,
  OpticalFormBookletQuestion,
  OpticalFormBooklet,
  OpticalQuestionReviewRequestPayload,
  ExamBookletMappingResponse,
} from './core/_models'
import {
  finalizeOpticalForms,
  getExamPeriods,
  getExamsByPeriod,
  processOpticalForms,
  saveOpticalExamResult,
  getOpticalQuestionReview,
  importExamBookletMappings,
  updateExamBookletMappings,
} from './core/_requests'
import './ExamOpticalReadingPage.scss'

import type {
  OpticalFormFinalizeExamPayload,
  OpticalFormFinalizeEntryPayload,
} from './core/_models'

interface ExamPeriodOption {
  id: number
  name: string
}

interface ExamOption {
  id: number
  name: string
  studentCount?: number | null
  alreadyScheduled?: boolean
  referenceId?: number | null
  baseExamId?: number | null
  hasOpticalReading?: boolean | null
}

interface SelectOption {
  value: number
  label: string
}

type BookletOptionMapping = {
  letterToOptionId: Record<string, number>
  optionIdToLetter: Record<number, string>
}

type QuestionOptionMappings = Record<string, BookletOptionMapping>

type ExamQuestionOptionMappings = Record<number, QuestionOptionMappings>

type ExamOptionMappings = Record<number, ExamQuestionOptionMappings>

type ExamQuestionAssignmentMap = Record<number, ExamQuestionAssignment>

type ExamAssignmentsState = Record<number, ExamQuestionAssignmentMap>

type QuestionPreviewSource = 'summary' | 'settings' | 'detail'

type QuestionPreviewContext = {
  examId: number
  examQuestionId: number
  source: QuestionPreviewSource
  bookletCode?: string | null
}

type BookletQuestionOrderMap = Record<number, number>

type ExamBookletQuestionOrders = Record<string, BookletQuestionOrderMap>

type ExamBookletOrdersState = Record<number, ExamBookletQuestionOrders>

type BookletOrderListState = Record<string, number[]>

type BookletOptionOrdersState = Record<string, Record<number, number[]>>

type MetricInsight = {
  label: string
  needsReview: boolean
  warn: boolean
}

type QuestionReviewCacheEntry = {
  comment?: string
  formattedHtml?: string
  error?: string
  signature: string
}

type NormalizedReviewOption = {
  id: number | null
  key: string | null
  label: string
  text: string
  isCorrect: boolean
}

type QuestionReviewDataContext = {
  examId: number
  analysis: OpticalFormQuestionAnalysis
  difficultyInsight: MetricInsight
  discriminationInsight: MetricInsight
  questionAssignments: ExamAssignmentsState
  optionMappings: ExamOptionMappings
  examConfigurations: Record<number, ExamConfiguration>
}

type QuestionReviewDataResult = {
  cacheKey: string
  signature: string
  payload: OpticalQuestionReviewRequestPayload
}

type BookletMappingModalState = {
  exam: OpticalFormExamResult
  orders: BookletOrderListState
  activeCode: string | null
  optionOrders: BookletOptionOrdersState
  selectedQuestionId: number | null
  uploadFile: File | null
  isSaving: boolean
  isImporting: boolean
}

const AI_COMMENT_PREVIEW_STYLE: React.CSSProperties = {
  maxHeight: '18rem',
  overflowY: 'auto',
  fontSize: '0.875rem',
  lineHeight: 1.6,
}

const isAiActive = process.env.REACT_APP_YZACTIVE === '1'

const CRONBACH_ALPHA_TOOLTIP: React.ReactNode = (
  <div className='text-start'>
    <p className='mb-2'>
      Cronbach Alpha, bir testin sorularının aynı yapıyı ölçme derecesini gösteren iç tutarlılık katsayısıdır.
      Katsayı ne kadar yüksekse, maddelerin birbirini desteklediği ve testin güvenilirliğinin o kadar güçlü olduğu
      kabul edilir.
    </p>
    <p className='mb-2'>
      Değerler 0 ile 1 arasında değişir. 0&#39;a yakın değerler soruların farklı yapıları ölçtüğünü, 1&#39;e yaklaşan
      değerler ise soruların aynı yapıyı güçlü biçimde hedeflediğini gösterir.
    </p>
    <ul className='mb-2 ps-4'>
      <li>0,95 ve üzeri: Aşırı yüksek güvenilirlik – maddeler birbirine çok benzer olabilir.</li>
      <li>0,90 - 0,94 arası: Çok yüksek güvenilirlik – tutarlılık oldukça güçlüdür.</li>
      <li>0,80 - 0,89 arası: İyi güvenilirlik – test genel olarak tutarlıdır.</li>
      <li>0,70 - 0,79 arası: Kabul edilebilir güvenilirlik – standart uygulamalar için yeterlidir.</li>
      <li>0,60 - 0,69 arası: Sınırda güvenilirlik – maddeleri gözden geçirmek faydalı olabilir.</li>
      <li>0,40 - 0,59 arası: Düşük güvenilirlik – tutarlılık sorunları belirgindir.</li>
      <li>0,40&#39;ın altı: Kabul edilemez – test güvenirliği yetersizdir.</li>
    </ul>
    <p className='mb-0'>
      Negatif değerlere düşen Cronbach Alpha, maddelerin ters puanlanmış olabileceğini; 0 değeri ise maddelerin ortak
      varyans üretmediğini gösterir.
    </p>
  </div>
)

const KR20_TOOLTIP: React.ReactNode = (
  <div className='text-start'>
    <p className='mb-2'>
      KR-20, doğru/yanlış şeklinde puanlanan maddelerden oluşan testlerin iç tutarlılığını ölçen güvenirlik katsayısıdır.
      Değer Cronbach Alpha ile aynı aralıkta yorumlanır ve 1&#39;e yaklaştıkça testin güvenilirliğinin arttığını gösterir.
    </p>
    <p className='mb-0'>
      0&#39;a yakın değerler maddelerin tutarlılığının zayıf olduğunu, 1&#39;e yaklaşan değerler ise maddelerin aynı yapıyı güçlü
      biçimde hedeflediğini belirtir.
    </p>
  </div>
)

const buildQuestionReviewData = ({
  examId,
  analysis,
  difficultyInsight,
  discriminationInsight,
  questionAssignments,
  optionMappings,
  examConfigurations,
}: QuestionReviewDataContext): QuestionReviewDataResult => {
  const cacheKey = `${examId}-${analysis.examQuestionId}`
  const assignment = questionAssignments[examId]?.[analysis.examQuestionId]
  const optionMapping = optionMappings[examId]?.[analysis.examQuestionId]
  const baseMapping = optionMapping?.[BASE_OPTION_MAPPING_KEY]

  const normalizedOptions: NormalizedReviewOption[] = Array.isArray(assignment?.options)
    ? assignment.options.map((option, index) => {
        const optionId = typeof option.id === 'number' && Number.isFinite(option.id) ? option.id : null
        const mappedLetter = optionId !== null ? mapOptionIdsToLetters([optionId], baseMapping)[0] : undefined
        const fallbackLabel = option.key ?? getLetterForIndex(index)
        const normalizedLabel = normalizeOptionValue(mappedLetter ?? fallbackLabel)
        const label = normalizedLabel ?? (fallbackLabel || `Seçenek ${index + 1}`)

        return {
          id: optionId,
          key: option.key ?? null,
          label,
          text: sanitizeRichText(option.name),
          isCorrect: Boolean(option.isTrue),
        }
      })
    : []

  const correctOptionLabels = normalizeAcceptedOptionsList(
    normalizedOptions
      .filter(option => option.isCorrect)
      .map(option => option.label || option.key || '')
  )

  const baseQuestionText = assignment?.questionName ? sanitizeRichText(assignment.questionName) : ''
  const normalizedGroupType =
    typeof assignment?.groupType === 'string' ? assignment.groupType.toLowerCase() : ''
  const isItemSetQuestionType =
    typeof assignment?.questionTypeId === 'number' &&
    assignment.questionTypeId === ITEM_SET_QUESTION_TYPE_ID
  const shouldIncludeSharedContent =
    isItemSetQuestionType || normalizedGroupType === 'itemset'
  const sharedContentTitle =
    shouldIncludeSharedContent && assignment?.sharedContentTitle
      ? sanitizeRichText(assignment.sharedContentTitle)
      : ''
  const sharedContentContent =
    shouldIncludeSharedContent && assignment?.sharedContentContent
      ? sanitizeRichText(assignment.sharedContentContent)
      : ''

  const questionTextSegments: string[] = []
  if (sharedContentTitle) {
    questionTextSegments.push(sharedContentTitle)
  }
  if (sharedContentContent) {
    questionTextSegments.push(sharedContentContent)
  }
  if (baseQuestionText) {
    questionTextSegments.push(baseQuestionText)
  }

  const questionText = questionTextSegments.join('\n\n')

  const payload: OpticalQuestionReviewRequestPayload = {
    examId,
    examQuestionId: analysis.examQuestionId,
    difficulty: typeof analysis.difficulty === 'number' ? analysis.difficulty : null,
    discrimination: typeof analysis.discrimination === 'number' ? analysis.discrimination : null,
    respondentCount: analysis.respondentCount,
    correctCount: analysis.correctCount,
    blankCount: analysis.blankCount,
    difficultyLabel: difficultyInsight.label,
    discriminationLabel: discriminationInsight.label,
    questionText: questionText || null,
    options:
      normalizedOptions.length > 0
        ? normalizedOptions.map(option => ({
            label: option.label,
            text: option.text,
            isCorrect: option.isCorrect,
            key: option.key,
            optionId: option.id,
          }))
        : undefined,
    correctOptionLabels: correctOptionLabels.length > 0 ? correctOptionLabels : undefined,
  }

  const signaturePayload = {
    questionText,
    options: normalizedOptions.map(option => ({
      id: option.id,
      key: option.key,
      label: option.label,
      text: option.text,
      isCorrect: option.isCorrect,
    })),
    correctOptionLabels,
  }

  const signature = [
    typeof analysis.difficulty === 'number' ? analysis.difficulty.toString() : 'null',
    typeof analysis.discrimination === 'number' ? analysis.discrimination.toString() : 'null',
    analysis.respondentCount.toString(),
    analysis.correctCount.toString(),
    analysis.blankCount.toString(),
    difficultyInsight.label,
    discriminationInsight.label,
    JSON.stringify(signaturePayload),
  ].join('|')

  return {cacheKey, signature, payload}
}

const baseDifficultyInsight = (value?: number | null): MetricInsight => {
  if (typeof value !== 'number') {
    return {label: 'Veri yok', needsReview: false, warn: false}
  }

  if (value < 0.2) {
    return {label: 'Çok zor madde ❗', needsReview: true, warn: true}
  }

  if (value < 0.4) {
    return {label: 'Zor madde', needsReview: false, warn: false}
  }

  if (value <= 0.59) {
    return {label: 'Orta güçlükte (ideal aralık ✅)', needsReview: false, warn: false}
  }

  if (value < 0.8) {
    return {label: 'Kolay madde', needsReview: false, warn: false}
  }

  return {label: 'Çok kolay madde ❗', needsReview: true, warn: true}
}

const baseDiscriminationInsight = (value?: number | null): MetricInsight => {
  if (typeof value !== 'number') {
    return {label: 'Veri yok', needsReview: false, warn: false}
  }

  if (value < 0) {
    return {label: 'Ters ayırt edici, ciddi problem ❌', needsReview: true, warn: true}
  }

  if (value < 0.2) {
    return {label: 'Zayıf ❗', needsReview: true, warn: true}
  }

  if (value < 0.3) {
    return {label: 'Kabul edilebilir', needsReview: false, warn: false}
  }

  if (value < 0.4) {
    return {label: 'İyi', needsReview: false, warn: false}
  }

  return {label: 'Çok iyi ayırt edicilik ✅', needsReview: false, warn: false}
}

const resolveDifficultyInsight = (analysis: OpticalFormQuestionAnalysis): MetricInsight => {
  const base = baseDifficultyInsight(analysis.difficulty)
  const label =
    typeof analysis.difficultyLabel === 'string' && analysis.difficultyLabel.trim().length > 0
      ? analysis.difficultyLabel
      : base.label
  const needsReview =
    typeof analysis.difficultyNeedsReview === 'boolean'
      ? analysis.difficultyNeedsReview
      : base.needsReview
  const warn = needsReview || base.warn
  return {label, needsReview, warn}
}

const resolveDiscriminationInsight = (analysis: OpticalFormQuestionAnalysis): MetricInsight => {
  const base = baseDiscriminationInsight(analysis.discrimination)
  const label =
    typeof analysis.discriminationLabel === 'string' &&
    analysis.discriminationLabel.trim().length > 0
      ? analysis.discriminationLabel
      : base.label
  const needsReview =
    typeof analysis.discriminationNeedsReview === 'boolean'
      ? analysis.discriminationNeedsReview
      : base.needsReview
  const warn = needsReview || base.warn
  return {label, needsReview, warn}
}

const getInsightClassName = (insight: MetricInsight): string => {
  if (insight.needsReview) {
    return 'text-danger fw-semibold'
  }

  if (insight.warn) {
    return 'text-warning fw-semibold'
  }

  return 'text-muted'
}

type QuestionOrderResolver = (questionId: number, fallback?: number | null) => number | null

const preferQuestionRecord = (
  current: OpticalFormQuestion | undefined,
  candidate: OpticalFormQuestion
): OpticalFormQuestion => {
  if (!current) {
    return candidate
  }

  const currentComment =
    typeof current.aiReviewComment === 'string' && current.aiReviewComment.trim().length > 0
  const candidateComment =
    typeof candidate.aiReviewComment === 'string' && candidate.aiReviewComment.trim().length > 0

  if (!currentComment && candidateComment) {
    return candidate
  }

  if (currentComment && !candidateComment) {
    return current
  }

  const currentNumber =
    typeof current.questionNumber === 'number' && Number.isFinite(current.questionNumber)
      ? current.questionNumber
      : Number.POSITIVE_INFINITY
  const candidateNumber =
    typeof candidate.questionNumber === 'number' && Number.isFinite(candidate.questionNumber)
      ? candidate.questionNumber
      : Number.POSITIVE_INFINITY

  if (candidateNumber < currentNumber) {
    return candidate
  }

  return current
}

const deduplicateQuestionList = (
  rawQuestions: OpticalFormQuestion[],
  resolveOrder?: QuestionOrderResolver
): OpticalFormQuestion[] => {
  const questionMap = new Map<number, OpticalFormQuestion>()

  rawQuestions.forEach(question => {
    if (!question || typeof question.examQuestionId !== 'number') {
      return
    }

    const preferred = preferQuestionRecord(questionMap.get(question.examQuestionId), question)
    questionMap.set(question.examQuestionId, preferred)
  })

  const orderResolver = resolveOrder
    ? (question: OpticalFormQuestion): number | null =>
        resolveOrder(
          question.examQuestionId,
          typeof question.questionNumber === 'number' ? question.questionNumber : null
        )
    : (question: OpticalFormQuestion): number | null =>
        typeof question.questionNumber === 'number' && Number.isFinite(question.questionNumber)
          ? question.questionNumber
          : null

  const normalized = Array.from(questionMap.values())

  normalized.sort((a, b) => {
    const multiA = a.isMultipleChoice ? 0 : 1
    const multiB = b.isMultipleChoice ? 0 : 1
    if (multiA !== multiB)
    {
      return multiA - multiB
    }

    const orderA = orderResolver(a)
    const orderB = orderResolver(b)

    if (orderA == null && orderB == null) {
      return a.examQuestionId - b.examQuestionId
    }

    if (orderA == null) {
      return 1
    }

    if (orderB == null) {
      return -1
    }

    if (orderA !== orderB) {
      return orderA - orderB
    }

    return a.examQuestionId - b.examQuestionId
  })

  return normalized.map(question => {
    const resolvedNumber = orderResolver(question)
    return {
      ...question,
      questionNumber:
        typeof resolvedNumber === 'number' && Number.isFinite(resolvedNumber)
          ? resolvedNumber
          : question.questionNumber,
    }
  })
}

const preferAnalysisRecord = (
  current: OpticalFormQuestionAnalysis | undefined,
  candidate: OpticalFormQuestionAnalysis
): OpticalFormQuestionAnalysis => {
  if (!current) {
    return candidate
  }

  const currentComment =
    typeof current.aiReviewComment === 'string' && current.aiReviewComment.trim().length > 0
  const candidateComment =
    typeof candidate.aiReviewComment === 'string' && candidate.aiReviewComment.trim().length > 0

  if (!currentComment && candidateComment) {
    return candidate
  }

  if (currentComment && !candidateComment) {
    return current
  }

  const currentNumber =
    typeof current.questionNumber === 'number' && Number.isFinite(current.questionNumber)
      ? current.questionNumber
      : Number.POSITIVE_INFINITY
  const candidateNumber =
    typeof candidate.questionNumber === 'number' && Number.isFinite(candidate.questionNumber)
      ? candidate.questionNumber
      : Number.POSITIVE_INFINITY

  if (candidateNumber < currentNumber) {
    return candidate
  }

  return current
}

const deduplicateQuestionAnalyses = (
  analyses: OpticalFormQuestionAnalysis[],
  resolveOrder: QuestionOrderResolver,
  questionTypeLookup?: Record<number, boolean>
): OpticalFormQuestionAnalysis[] => {
  const analysisMap = new Map<number, OpticalFormQuestionAnalysis>()

  analyses.forEach(analysis => {
    if (!analysis || typeof analysis.examQuestionId !== 'number') {
      return
    }

    const preferred = preferAnalysisRecord(
      analysisMap.get(analysis.examQuestionId),
      analysis
    )
    analysisMap.set(analysis.examQuestionId, preferred)
  })

  const normalized = Array.from(analysisMap.values())

  normalized.sort((a, b) => {
    const multiRank = (examQuestionId: number): number => {
      if (!questionTypeLookup) {
        return 0
      }

      return questionTypeLookup[examQuestionId] ? 0 : 1
    }

    const multiA = multiRank(a.examQuestionId)
    const multiB = multiRank(b.examQuestionId)

    if (multiA !== multiB) {
      return multiA - multiB
    }

    const orderA = resolveOrder(a.examQuestionId, a.questionNumber)
    const orderB = resolveOrder(b.examQuestionId, b.questionNumber)

    if (orderA == null && orderB == null) {
      return a.examQuestionId - b.examQuestionId
    }

    if (orderA == null) {
      return 1
    }

    if (orderB == null) {
      return -1
    }

    if (orderA !== orderB) {
      return orderA - orderB
    }

    return a.examQuestionId - b.examQuestionId
  })

  return normalized.map(analysis => {
    const resolvedNumber = resolveOrder(analysis.examQuestionId, analysis.questionNumber)
    const baseNumber =
      typeof analysis.questionNumber === 'number' && Number.isFinite(analysis.questionNumber)
        ? analysis.questionNumber
        : analysis.examQuestionId
    return {
      ...analysis,
      questionNumber:
        typeof resolvedNumber === 'number' && Number.isFinite(resolvedNumber)
          ? resolvedNumber
          : baseNumber,
    }
  })
}

const buildMasterOrderMap = (
  booklets: OpticalFormBooklet[],
  masterBookletCode: string | null
): Record<number, number> => {
  const normalizedMaster = normalizeBookletCode(masterBookletCode)

  const targetBooklet =
    booklets.find(booklet => normalizeBookletCode(booklet.code) === normalizedMaster) ??
    booklets.find(booklet => normalizeBookletCode(booklet.code) === MASTER_BOOKLET_CODE) ??
    booklets[0]

  if (!targetBooklet || !Array.isArray(targetBooklet.questions)) {
    return {}
  }

  return targetBooklet.questions.reduce<Record<number, number>>((acc, question, index) => {
    if (!question || typeof question.examQuestionId !== 'number') {
      return acc
    }

    const preferredNumber =
      typeof question.questionNumber === 'number' && Number.isFinite(question.questionNumber)
        ? question.questionNumber
        : index + 1

    acc[question.examQuestionId] = preferredNumber
    return acc
  }, {})
}

const cloneExamBooklets = (booklets: OpticalFormBooklet[]): OpticalFormBooklet[] =>
  Array.isArray(booklets)
    ? booklets.map(booklet => ({
        ...booklet,
        questions: Array.isArray(booklet.questions)
          ? booklet.questions
              .filter((question): question is OpticalFormBookletQuestion => Boolean(question))
              .map(question => ({...question}))
          : [],
      }))
    : []

const remapEntryToBooklet = (
  entry: OpticalFormStudentEntry,
  exam: OpticalFormExamResult | undefined,
  bookletCode: string
): OpticalFormStudentEntry => {
  if (!exam || !Array.isArray(exam.booklets) || exam.booklets.length === 0) {
    return entry
  }

  const normalizedCode = normalizeBookletCode(bookletCode)
  const normalizedMaster = normalizeBookletCode(exam.masterBookletCode)

  const targetBooklet =
    exam.booklets.find(booklet => normalizeBookletCode(booklet.code) === normalizedCode) ??
    exam.booklets.find(booklet => normalizeBookletCode(booklet.code) === normalizedMaster) ??
    exam.booklets[0]

  if (!targetBooklet || !Array.isArray(targetBooklet.questions) || targetBooklet.questions.length === 0) {
    return entry
  }

  const questionMap = targetBooklet.questions.reduce<Record<number, number>>((acc, question) => {
    const numberValue = Number(question?.questionNumber)
    const examQuestionId = Number(question?.examQuestionId)

    if (Number.isFinite(numberValue) && Number.isFinite(examQuestionId)) {
      acc[numberValue] = examQuestionId
    }

    return acc
  }, {})

  let hasChanges = false

  const remappedDetails = entry.answerDetails.map(detail => {
    const targetExamQuestionId = questionMap[detail.questionNumber]

    if (Number.isFinite(targetExamQuestionId) && targetExamQuestionId !== detail.examQuestionId) {
      hasChanges = true
      return {
        ...detail,
        examQuestionId: targetExamQuestionId as number,
      }
    }

    return detail
  })

  return hasChanges ? {...entry, answerDetails: remappedDetails} : entry
}

const normalizeExamResult = (exam: OpticalFormExamResult): OpticalFormExamResult => {
  const normalizedEntries = Array.isArray(exam.entries)
    ? exam.entries.map(entry => ({
        ...entry,
        candidates: Array.isArray(entry.candidates) ? entry.candidates : [],
        answerDetails: Array.isArray(entry.answerDetails) ? entry.answerDetails : [],
        previousStatus: entry.previousStatus ?? null,
        previousIsPrimaryMatch: entry.previousIsPrimaryMatch ?? null,
        examTypeCode: entry.examTypeCode ?? '',
        originalBookletCode: entry.originalBookletCode ?? entry.bookletCode ?? '',
        originalStudentNumber: entry.originalStudentNumber ?? entry.studentNumber ?? '',
      }))
    : []

  const normalizedBooklets = Array.isArray(exam.booklets)
    ? exam.booklets.map(booklet => ({
        ...booklet,
        questions: Array.isArray(booklet.questions) ? booklet.questions : [],
      }))
    : []

  const normalizedMasterBookletCode = normalizeBookletCode(exam.masterBookletCode)
  const masterOrderMap = buildMasterOrderMap(normalizedBooklets, normalizedMasterBookletCode)

  const questionOrderResolver: QuestionOrderResolver = (questionId, fallback) => {
    const masterOrder = masterOrderMap[questionId]
    if (typeof masterOrder === 'number' && Number.isFinite(masterOrder)) {
      return masterOrder
    }

    if (typeof fallback === 'number' && Number.isFinite(fallback)) {
      return fallback
    }

    return null
  }

  const normalizedQuestions = deduplicateQuestionList(
    Array.isArray(exam.questions) ? exam.questions : [],
    questionOrderResolver
  )

  const questionTypeLookup = normalizedQuestions.reduce<Record<number, boolean>>(
    (acc, question) => {
      if (typeof question.examQuestionId === 'number') {
        acc[question.examQuestionId] = Boolean(question.isMultipleChoice)
      }

      return acc
    },
    {}
  )

  const normalizedSummary = exam.summary
    ? {
        ...exam.summary,
        questionAnalyses: Array.isArray(exam.summary.questionAnalyses)
          ? deduplicateQuestionAnalyses(
              exam.summary.questionAnalyses,
              questionOrderResolver,
              questionTypeLookup
            )
          : [],
      }
    : null

  return {
    ...exam,
    questions: normalizedQuestions,
    students: Array.isArray(exam.students) ? exam.students : [],
    entries: normalizedEntries,
    missingStudents: Array.isArray(exam.missingStudents) ? exam.missingStudents : [],
    booklets: normalizedBooklets,
    summary: normalizedSummary,
    masterBookletCode: normalizedMasterBookletCode,
  }
}

const applyEntriesUpdate = (
  exam: OpticalFormExamResult,
  updater: (entries: OpticalFormStudentEntry[]) => OpticalFormStudentEntry[]
): OpticalFormExamResult => {
  const clonedEntries = exam.entries.map(entry => ({
    ...entry,
    candidates: [...entry.candidates],
    answerDetails: [...entry.answerDetails],
  }))

  const updatedEntries = updater(clonedEntries)
  const matchedIds = new Set<number>()

  updatedEntries.forEach(entry => {
    if (
      entry.status === OpticalStudentEntryStatus.Matched &&
      entry.studentSemesterCourseId &&
      entry.isPrimaryMatch
    ) {
      matchedIds.add(entry.studentSemesterCourseId)
    }
  })

  const missingStudents = exam.students.filter(
    student => !matchedIds.has(student.studentSemesterCourseId)
  )

  return {
    ...exam,
    entries: updatedEntries,
    missingStudents,
  }
}

type QuestionAcceptedOptionsMap = Record<string, string[]>

type ExamQuestionConfig = {
  examQuestionId: number
  acceptedOptionsByBooklet: QuestionAcceptedOptionsMap
  defaultAcceptedOptions: string[]
  isCancelled: boolean
  isMultipleChoice: boolean
  point: number
}

type ExamPenaltyConfig = {
  applyPenalty: boolean
  wrongCount: number
  correctDeduction: number
}

type ExamConfiguration = {
  questions: Record<number, ExamQuestionConfig>
  penalty: ExamPenaltyConfig
  note: string
  masterBookletCode: string | null
  selectedBookletCode: string | null
}

const normalizeOptionValue = (value?: string | null): string | null => {
  if (!value) {
    return null
  }

  return value.trim().toUpperCase()
}

const roundValue = (value: number, digits = 2): number => {
  const factor = Math.pow(10, digits)
  return Math.round(value * factor) / factor
}

const normalizeBookletCode = (code?: string | null): string | null => {
  if (!code) {
    return null
  }

  const normalized = code.trim().toUpperCase()
  return normalized || null
}

const normalizeAcceptedOptionsList = (options?: string[] | null): string[] => {
  if (!options || options.length === 0) {
    return []
  }

  return Array.from(
    new Set(
      options
        .map(option => normalizeOptionValue(option))
        .filter((option): option is string => Boolean(option))
    )
  )
}

const formatAcceptedOptionsInput = (rawValue: string, normalizedOptions: string[]): string => {
  const canonicalValue = normalizedOptions.join(', ')
  if (!rawValue) {
    return canonicalValue
  }

  if (normalizedOptions.length > 0 && /[;,\s]+$/.test(rawValue)) {
    return `${canonicalValue}, `
  }

  return canonicalValue
}

const cloneAcceptedOptionsMap = (map: QuestionAcceptedOptionsMap): QuestionAcceptedOptionsMap => {
  const cloned: QuestionAcceptedOptionsMap = {}
  Object.entries(map).forEach(([key, value]) => {
    cloned[key] = [...value]
  })
  return cloned
}

const ITEM_SET_QUESTION_TYPE_ID = 4
const OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const BASE_OPTION_MAPPING_KEY = '__BASE__'
const MASTER_BOOKLET_CODE = '__MASTER__'

const getLetterForIndex = (index: number): string => {
  if (index >= 0 && index < OPTION_LETTERS.length) {
    return OPTION_LETTERS[index]
  }
  return String(index + 1)
}

const filterOptionOrder = (order?: number[] | null): number[] => {
  if (!Array.isArray(order)) {
    return []
  }
  return order.filter(optionId => typeof optionId === 'number' && Number.isFinite(optionId))
}

const createOptionMappingFromOrder = (order: number[]): BookletOptionMapping => {
  const letterToOptionId: Record<string, number> = {}
  const optionIdToLetter: Record<number, string> = {}

  order.forEach((optionId, index) => {
    const letter = getLetterForIndex(index)
    letterToOptionId[letter] = optionId
    optionIdToLetter[optionId] = letter
  })

  return {letterToOptionId, optionIdToLetter}
}

const resolveVariantOrder = (
  question: ExamQuestionAssignment,
  normalizedCode: string
): number[] => {
  const variantMap = question.optionOrderByBooklet ?? {}
  const direct = variantMap[normalizedCode]
  if (Array.isArray(direct) && direct.length > 0) {
    return filterOptionOrder(direct)
  }

  for (const [code, order] of Object.entries(variantMap)) {
    const normalized = normalizeBookletCode(code)
    if (normalized && normalized === normalizedCode && Array.isArray(order) && order.length > 0) {
      return filterOptionOrder(order)
    }
  }

  return []
}

const buildQuestionOptionMappings = (
  question: ExamQuestionAssignment,
  exam: OpticalFormExamResult
): QuestionOptionMappings => {
  const mappings: QuestionOptionMappings = {}
  const defaultOrder = (() => {
    const preferred = filterOptionOrder(question.optionOrder)
    if (preferred.length > 0) {
      return preferred
    }
    if (!Array.isArray(question.options)) {
      return []
    }
    return question.options
      .map(option => option?.id)
      .filter((optionId): optionId is number => typeof optionId === 'number' && Number.isFinite(optionId))
  })()

  mappings[BASE_OPTION_MAPPING_KEY] = createOptionMappingFromOrder(defaultOrder)

  const allCodes = new Set<string>()
  const variantMap = question.optionOrderByBooklet ?? {}

  Object.keys(variantMap).forEach(code => {
    const normalized = normalizeBookletCode(code)
    if (normalized) {
      allCodes.add(normalized)
    }
  })

  if (Array.isArray(exam.booklets)) {
    exam.booklets.forEach(booklet => {
      const normalized = normalizeBookletCode(booklet.code)
      if (normalized) {
        allCodes.add(normalized)
      }
    })
  }

  allCodes.forEach(code => {
    const variantOrder = resolveVariantOrder(question, code)
    const effectiveOrder = variantOrder.length > 0 ? variantOrder : defaultOrder
    mappings[code] = createOptionMappingFromOrder(effectiveOrder)
  })

  if (mappings[BASE_OPTION_MAPPING_KEY]) {
    mappings[MASTER_BOOKLET_CODE] = mappings[BASE_OPTION_MAPPING_KEY]
  }

  const normalizedMaster = normalizeBookletCode(exam.masterBookletCode)
  if (normalizedMaster && !mappings[normalizedMaster] && mappings[BASE_OPTION_MAPPING_KEY]) {
    mappings[normalizedMaster] = mappings[BASE_OPTION_MAPPING_KEY]
  }

  return mappings
}

const mapLettersToOptionIds = (
  letters: string[],
  mapping?: BookletOptionMapping
): number[] => {
  if (!mapping) {
    return []
  }

  const unique = new Set<number>()
  letters.forEach(letter => {
    const normalized = normalizeOptionValue(letter)
    if (!normalized) {
      return
    }
    const optionId = mapping.letterToOptionId[normalized]
    if (typeof optionId === 'number' && Number.isFinite(optionId)) {
      unique.add(optionId)
    }
  })

  return Array.from(unique)
}

const mapOptionIdsToLetters = (
  optionIds: number[],
  mapping?: BookletOptionMapping
): string[] => {
  if (!mapping) {
    return []
  }

  const letters = optionIds
    .map(optionId => mapping.optionIdToLetter[optionId])
    .filter((letter): letter is string => Boolean(letter))

  return normalizeAcceptedOptionsList(letters)
}

const stripWrappingParagraphTags = (html: string): string => {
  const trimmed = html.trim()
  if (!trimmed) {
    return ''
  }

  const match = trimmed.match(/^<p[^>]*>([\s\S]*)<\/p>$/i)
  if (match) {
    return match[1].trim()
  }

  return trimmed
}

const removeTrailingEmptyHtml = (html: string): string => {
  let output = html.trim()
  if (!output) {
    return ''
  }

  const trailingPattern =
    /(?:\s|&nbsp;|&#160;|<br\s*\/?>|<p[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>)+$/gi

  while (true) {
    const next = output.replace(trailingPattern, '').trim()
    if (next === output) {
      break
    }
    output = next
  }

  return output
}

const enhanceQuestionHtml = (html?: string | null): string => {
  if (!html) {
    return ''
  }

  let normalized = html.trim()
  normalized = removeTrailingEmptyHtml(normalized)
  normalized = stripWrappingParagraphTags(normalized)
  normalized = removeTrailingEmptyHtml(normalized)

  return normalized
}

const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")

const sanitizeRichText = (value?: string | null): string => {
  if (!value) {
    return ''
  }

  const enhanced = enhanceQuestionHtml(value)
  const withoutTags = enhanced.replace(/<[^>]*>/g, ' ')
  return decodeHtmlEntities(withoutTags).replace(/\s+/g, ' ').trim()
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const formatLineWithBasicMarkdown = (value: string): string =>
  escapeHtml(value).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

const formatAiCommentHtml = (comment: string): string => {
  const normalized = comment.replace(/\r\n/g, '\n').trim()
  if (!normalized) {
    return "<p class='mb-0 text-muted'>Yapay zekâdan yorum alınamadı.</p>"
  }

  const lines = normalized.split('\n')
  const blocks: string[] = []
  let currentList: {type: 'ul' | 'ol'; items: string[]} | null = null

  const flushList = () => {
    if (!currentList || currentList.items.length === 0) {
      currentList = null
      return
    }

    const tag = currentList.type
    const items = currentList.items
      .map(item => `<li class='mb-1'>${item}</li>`)
      .join('')
    blocks.push(`<${tag} class='mb-2 ps-4'>${items}</${tag}>`)
    currentList = null
  }

  lines.forEach(line => {
    const trimmed = line.trim()
    if (!trimmed) {
      flushList()
      return
    }

    const orderedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/)
    if (orderedMatch) {
      const content = formatLineWithBasicMarkdown(orderedMatch[2])
      if (!currentList || currentList.type !== 'ol') {
        flushList()
        currentList = {type: 'ol', items: []}
      }
      currentList.items.push(content)
      return
    }

    if (/^[-•]\s+/.test(trimmed)) {
      const content = formatLineWithBasicMarkdown(trimmed.replace(/^[-•]\s+/, ''))
      if (!currentList || currentList.type !== 'ul') {
        flushList()
        currentList = {type: 'ul', items: []}
      }
      currentList.items.push(content)
      return
    }

    flushList()
    blocks.push(`<p class='mb-2'>${formatLineWithBasicMarkdown(trimmed)}</p>`)
  })

  flushList()

  if (blocks.length === 0) {
    return "<p class='mb-0 text-muted'>Yapay zekâdan yorum alınamadı.</p>"
  }

  const lastIndex = blocks.length - 1
  blocks[lastIndex] = blocks[lastIndex].replace('mb-2', 'mb-0')

  return blocks.join('')
}

const getCronbachAlphaComment = (value?: number | null, k?: number): string | null => {
    if (value === null || value === undefined || Number.isNaN(value)) return null

    if (value < 0) return 'Negatif güvenilirlik: maddeler ters puanlanmış olabilir veya tutarlılık çok düşük.'
    if (value === 0) return 'Güvenilirlik yok (α=0): maddeler ortak varyans üretmiyor.'

    let base: string
    if (value >= 0.95) {
        base = 'Aşırı yüksek güvenilirlik (mükemmel); madde tekrarına veya aşırı benzerliğe işaret edebilir.'
    } else if (value >= 0.90) {
        base = 'Mükemmel güvenilirlik (çok yüksek tutarlılık).'
    } else if (value >= 0.80) {
        base = 'İyi güvenilirlik.'
    } else if (value >= 0.70) {
        base = 'Kabul edilebilir güvenilirlik.'
    } else if (value >= 0.60) {
        base = 'Sorgulanabilir/sınırda güvenilirlik (iyileştirme önerilir).'
    } else if (value >= 0.40) {
        base = 'Düşük güvenilirlik (geliştirme gereklidir.).'
    } else {
        base = 'Kabul edilemez güvenilirlik (ölçek yeniden tasarlanmalı).'
    }

    const notes: string[] = []
    if (typeof k === 'number' && k > 0 && k < 10) {
        notes.push('Madde sayısı az olduğunda alpha doğal olarak düşük çıkabilir (k<10).')
    }
    if (value >= 0.95) {
        notes.push('Aşırı benzer maddeleri azaltmak veya kapsamı genişletmek düşünülebilir.')
    } else if (value < 0.60) {
        notes.push('Düşük madde-toplam korelasyonlu maddeleri ayıklamayı ve madde sayısını artırmayı düşünün.')
    }

    return notes.length ? `${base} ${notes.join(' ')}` : base
}

const getLearningOutcomeTypeLabel = (type?: string | null): string => {
  if (!type) {
    return 'Öğrenme Çıktısı'
  }

  const normalized = type.toLowerCase()

  if (normalized === 'department' || normalized === 'boarddepartment') {
    return 'Anabilim Dalı'
  }

  if (
    normalized === 'assignmentoutcome' ||
    normalized === 'learningobjective' ||
    normalized === 'outcomegroup'
  ) {
    return 'Öğrenme Çıktısı'
  }

  if (normalized === 'organizationoutcome') {
    return 'Program Öğrenme Çıktısı'
  }

  if (normalized === 'courseoutcome') {
    return 'Ders Öğrenme Çıktısı'
  }

  return type
}


const getAcceptedOptionsForBooklet = (
  questionConfig: ExamQuestionConfig,
  bookletCode: string | null,
  fallbackBookletCode: string | null
): string[] => {
  const acceptedMap = questionConfig.acceptedOptionsByBooklet
  const normalizedBooklet = normalizeBookletCode(bookletCode)
  if (normalizedBooklet && acceptedMap[normalizedBooklet]) {
    return [...acceptedMap[normalizedBooklet]]
  }

  const normalizedFallback = normalizeBookletCode(fallbackBookletCode)
  if (normalizedFallback && acceptedMap[normalizedFallback]) {
    return [...acceptedMap[normalizedFallback]]
  }

  if (questionConfig.defaultAcceptedOptions.length > 0) {
    return [...questionConfig.defaultAcceptedOptions]
  }

  return []
}

const buildQuestionConfig = (
  question: OpticalFormQuestion,
  bookletLookup: Record<string, Record<number, OpticalFormBookletQuestion>>
): ExamQuestionConfig => {
  const acceptedOptionsByBooklet: QuestionAcceptedOptionsMap = {}
  Object.entries(bookletLookup).forEach(([code, questions]) => {
    const bookletQuestion = questions[question.examQuestionId]
    if (bookletQuestion) {
      acceptedOptionsByBooklet[code] = normalizeAcceptedOptionsList(bookletQuestion.acceptedOptions)
    }
  })

  const baseOptions = normalizeAcceptedOptionsList(
    Array.isArray(question.acceptedOptions) && question.acceptedOptions.length > 0
      ? question.acceptedOptions
      : question.correctOption
        ? [question.correctOption]
        : []
  )

  return {
    examQuestionId: question.examQuestionId,
    acceptedOptionsByBooklet,
    defaultAcceptedOptions: baseOptions,
    isCancelled: Boolean(question.isCancelled),
    isMultipleChoice: Boolean(question.isMultipleChoice),
    point: Number(question.point ?? 0),
  }
}

const createInitialConfiguration = (exam: OpticalFormExamResult): ExamConfiguration => {
  const bookletLookup = exam.booklets.reduce<Record<string, Record<number, OpticalFormBookletQuestion>>>(
    (acc, booklet) => {
      const normalizedCode = normalizeBookletCode(booklet.code)
      if (!normalizedCode) {
        return acc
      }

      acc[normalizedCode] = booklet.questions.reduce<Record<number, OpticalFormBookletQuestion>>(
        (questionMap, bookletQuestion) => {
          questionMap[bookletQuestion.examQuestionId] = bookletQuestion
          return questionMap
        },
        {}
      )

      return acc
    },
    {}
  )

  const questions = exam.questions.reduce<Record<number, ExamQuestionConfig>>((acc, question) => {
    const config = buildQuestionConfig(question, bookletLookup)

    // Ensure every known booklet has an entry with at least the default options
    Object.keys(bookletLookup).forEach(code => {
      if (!config.acceptedOptionsByBooklet[code]) {
        config.acceptedOptionsByBooklet[code] = [...config.defaultAcceptedOptions]
      }
    })

    acc[config.examQuestionId] = config
    return acc
  }, {})

  Object.values(questions).forEach(question => {
    if (!question.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE]) {
      question.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE] = [...question.defaultAcceptedOptions]
    }
  })

  const normalizedMaster = normalizeBookletCode(exam.masterBookletCode)
  if (normalizedMaster) {
    Object.values(questions).forEach(question => {
      if (!question.acceptedOptionsByBooklet[normalizedMaster]) {
        question.acceptedOptionsByBooklet[normalizedMaster] = [
          ...question.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE],
        ]
      }
    })
  }

  const resolvedMaster = normalizedMaster ?? MASTER_BOOKLET_CODE

  return {
    questions,
    penalty: {
      applyPenalty: false,
      wrongCount: 4,
      correctDeduction: 1,
    },
    note: exam.objectionNote ?? '',
    masterBookletCode: resolvedMaster,
    selectedBookletCode: resolvedMaster,
  }
}

const cloneExamConfiguration = (config: ExamConfiguration): ExamConfiguration => {
  const normalizedMaster = normalizeBookletCode(config.masterBookletCode)
  const normalizedSelected = normalizeBookletCode(config.selectedBookletCode)

  const clonedQuestions = Object.values(config.questions).reduce<Record<number, ExamQuestionConfig>>(
    (acc, question) => {
      acc[question.examQuestionId] = {
        ...question,
        acceptedOptionsByBooklet: cloneAcceptedOptionsMap(question.acceptedOptionsByBooklet),
        defaultAcceptedOptions: [...question.defaultAcceptedOptions],
      }
      if (!acc[question.examQuestionId].acceptedOptionsByBooklet[MASTER_BOOKLET_CODE]) {
        acc[question.examQuestionId].acceptedOptionsByBooklet[MASTER_BOOKLET_CODE] = [
          ...acc[question.examQuestionId].defaultAcceptedOptions,
        ]
      }
      if (
        normalizedMaster &&
        !acc[question.examQuestionId].acceptedOptionsByBooklet[normalizedMaster]
      ) {
        acc[question.examQuestionId].acceptedOptionsByBooklet[normalizedMaster] = [
          ...acc[question.examQuestionId].acceptedOptionsByBooklet[MASTER_BOOKLET_CODE],
        ]
      }
      return acc
    },
    {}
  )

  const resolvedMaster = normalizedMaster ?? MASTER_BOOKLET_CODE
  const resolvedSelected = normalizedSelected ?? resolvedMaster

  return {
    questions: clonedQuestions,
    penalty: {
      applyPenalty: Boolean(config.penalty?.applyPenalty),
      wrongCount: Number(config.penalty?.wrongCount ?? 4),
      correctDeduction: Number(config.penalty?.correctDeduction ?? 1),
    },
    note: config.note ?? '',
    masterBookletCode: resolvedMaster,
    selectedBookletCode: resolvedSelected,
  }
}

const parseAcceptedOptionsInput = (value: string): string[] => {
  if (!value) {
    return []
  }

  const tokens = value
    .split(/[;,\s]+/)
    .map(token => normalizeOptionValue(token))
    .filter((token): token is string => Boolean(token))

  return Array.from(new Set(tokens))
}

const computeScalingFactor = (configs: ExamQuestionConfig[]): number => {
  const originalTotal = configs.reduce((sum, cfg) => sum + cfg.point, 0)
  const effectiveTotal = configs.filter(cfg => !cfg.isCancelled).reduce((sum, cfg) => sum + cfg.point, 0)

  if (originalTotal <= 0 || effectiveTotal <= 0) {
    return 1
  }

  return originalTotal / effectiveTotal
}

const computeAverageMultipleChoicePoint = (configs: ExamQuestionConfig[]): number => {
  const active = configs.filter(cfg => cfg.isMultipleChoice && !cfg.isCancelled)
  if (active.length === 0) {
    return 0
  }

  const total = active.reduce((sum, cfg) => sum + cfg.point, 0)
  return total / active.length
}

const recalculateEntry = (
  entry: OpticalFormStudentEntry,
  questionConfigMap: Record<number, ExamQuestionConfig>,
  scalingFactor: number,
  penaltyConfig: ExamPenaltyConfig,
  averagePenaltyPoint: number,
  masterBookletCode: string | null
): OpticalFormStudentEntry => {
  let correct = 0
  let wrong = 0
  let blank = 0
  let wrongMultipleChoice = 0
  let totalPoint = 0

  const entryBookletCode = normalizeBookletCode(entry.bookletCode)

  const updatedDetails = entry.answerDetails.map(detail => {
    const questionConfig = questionConfigMap[detail.examQuestionId]

    const acceptedCandidates = questionConfig
      ? getAcceptedOptionsForBooklet(questionConfig, entryBookletCode, masterBookletCode)
      : Array.isArray(detail.acceptedOptions) && detail.acceptedOptions.length > 0
        ? detail.acceptedOptions
        : detail.correctOption
          ? [detail.correctOption]
          : []

    const acceptedOptions = Array.from(
      new Set(
        acceptedCandidates
          .map(option => normalizeOptionValue(option))
          .filter((option): option is string => Boolean(option))
      )
    )

    const normalizedStudentOption = normalizeOptionValue(detail.studentOption)
    const pointValue = questionConfig ? questionConfig.point : Number(detail.maxPoint ?? 0)
    const isMultipleChoice = questionConfig ? questionConfig.isMultipleChoice : Boolean(detail.isMultipleChoice)
    const isCancelled = questionConfig ? questionConfig.isCancelled : Boolean(detail.isCancelled)

    let isCorrect = false
    let point = 0

    if (isCancelled)
    {
      isCorrect = true
    }
    else if (!normalizedStudentOption)
    {
      blank += 1
    }
    else if (acceptedOptions.includes(normalizedStudentOption))
    {
      isCorrect = true
      point = pointValue
      correct += 1
    }
    else
    {
      wrong += 1
      if (isMultipleChoice)
      {
        wrongMultipleChoice += 1
      }
    }

    totalPoint += point

    return {
      ...detail,
      isCorrect,
      point,
      maxPoint: pointValue,
      isCancelled,
      isMultipleChoice,
      acceptedOptions,
    }
  })

  if (
    penaltyConfig.applyPenalty &&
    penaltyConfig.wrongCount > 0 &&
    penaltyConfig.correctDeduction > 0 &&
    averagePenaltyPoint > 0
  )
  {
    const penaltyPerWrong =
      (averagePenaltyPoint * penaltyConfig.correctDeduction) / penaltyConfig.wrongCount
    const penaltyTotal = penaltyPerWrong * wrongMultipleChoice

    if (penaltyTotal > 0)
    {
      totalPoint -= penaltyTotal
    }
  }

  if (scalingFactor !== 1)
  {
    totalPoint *= scalingFactor
    for (const detail of updatedDetails)
    {
      detail.point = roundValue((detail.point ?? 0) * scalingFactor, 4)
      detail.maxPoint = detail.maxPoint != null ? roundValue(detail.maxPoint * scalingFactor, 4) : detail.maxPoint
    }
  }

  totalPoint = Math.max(0, totalPoint)

  return {
    ...entry,
    correctCount: correct,
    wrongCount: wrong,
    blankCount: blank,
    totalPoint: roundValue(totalPoint),
    answerDetails: updatedDetails,
  }
}

const recomputeMissingStudents = (
  students: OpticalFormExamStudent[],
  entries: OpticalFormStudentEntry[]
): OpticalFormExamStudent[] => {
  const matchedIds = new Set(
    entries
      .filter(entry => entry.status === OpticalStudentEntryStatus.Matched && entry.isPrimaryMatch && entry.studentSemesterCourseId != null)
      .map(entry => entry.studentSemesterCourseId as number)
  )

  return students
    .filter(student => !matchedIds.has(student.studentSemesterCourseId))
    .sort((a, b) => a.studentNumber.localeCompare(b.studentNumber))
}

const computeVariance = (values: number[]): number => {
  if (values.length <= 1)
  {
    return 0
  }

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length
  const sumSquares = values.reduce((sum, value) => {
    const diff = value - mean
    return sum + diff * diff
  }, 0)

  return sumSquares / (values.length - 1)
}

const computeCovariance = (xs: number[], ys: number[]): number => {
  const n = xs.length
  if (n <= 1 || ys.length !== n)
  {
    return 0
  }

  let meanX = 0
  let meanY = 0
  for (let i = 0; i < n; i++)
  {
    meanX += xs[i]
    meanY += ys[i]
  }
  meanX /= n
  meanY /= n

  let sum = 0
  for (let i = 0; i < n; i++)
  {
    sum += (xs[i] - meanX) * (ys[i] - meanY)
  }

  return sum / (n - 1)
}

const computeCorrelation = (xs: number[], ys: number[]): number => {
  const covariance = computeCovariance(xs, ys)
  const varianceX = computeVariance(xs)
  const varianceY = computeVariance(ys)

  if (varianceX <= 0 || varianceY <= 0)
  {
    return 0
  }

  return covariance / Math.sqrt(varianceX * varianceY)
}

type LearningOutcomeAggregate = {
  type: string
  outcomeId: number
  outcomeName: string
  order: number | null
  questionIds: Set<number>
  cronbachAlpha: number | null
  kr20: number | null
}

const buildSummaryFromEntries = (
  entries: OpticalFormStudentEntry[],
  rawQuestions: OpticalFormQuestion[],
  questionAssignments?: ExamQuestionAssignmentMap | null
): OpticalFormExamSummary => {
  const summary: OpticalFormExamSummary = {
    totalEntries: entries.length,
    matchedEntries: 0,
    duplicateEntries: 0,
    unregisteredEntries: 0,
    ambiguousEntries: 0,
    bookletMismatchEntries: 0,
    skippedEntries: 0,
    invalidEntries: 0,
    averageScore: null,
    medianScore: null,
    standardDeviation: null,
    cronbachAlpha: null,
    cronbachAlpha01: null,
    kr20: null,
    questionAnalyses: [],
    learningOutcomeReliabilities: [],
  }

  const assignmentOrderMap: Record<number, number> = {}
  if (questionAssignments && typeof questionAssignments === 'object')
  {
    Object.values(questionAssignments).forEach(assignment => {
      if (!assignment || typeof assignment.examQuestionId !== 'number')
      {
        return
      }

      const orderValue = Number(assignment.order)
      if (Number.isFinite(orderValue))
      {
        assignmentOrderMap[assignment.examQuestionId] = orderValue
      }
    })
  }

  const questionMap = new Map<number, OpticalFormQuestion>()

  rawQuestions.forEach(question => {
    if (!question || typeof question.examQuestionId !== 'number')
    {
      return
    }

    const existing = questionMap.get(question.examQuestionId)
    if (!existing)
    {
      questionMap.set(question.examQuestionId, question)
      return
    }

    const existingComment =
      typeof existing.aiReviewComment === 'string' && existing.aiReviewComment.trim().length > 0
    const candidateComment =
      typeof question.aiReviewComment === 'string' && question.aiReviewComment.trim().length > 0

    if (!existingComment && candidateComment)
    {
      questionMap.set(question.examQuestionId, question)
      return
    }

    if (existingComment && !candidateComment)
    {
      return
    }

    const existingNumber =
      typeof existing.questionNumber === 'number' && Number.isFinite(existing.questionNumber)
        ? existing.questionNumber
        : Number.POSITIVE_INFINITY
    const candidateNumber =
      typeof question.questionNumber === 'number' && Number.isFinite(question.questionNumber)
        ? question.questionNumber
        : Number.POSITIVE_INFINITY

    if (candidateNumber < existingNumber)
    {
      questionMap.set(question.examQuestionId, question)
    }
  })

  const normalizedQuestions = Array.from(questionMap.values())

  const resolveQuestionOrder = (questionId: number, fallback?: number | null): number | null => {
    const assignmentOrder = assignmentOrderMap[questionId]
    if (typeof assignmentOrder === 'number' && Number.isFinite(assignmentOrder))
    {
      return assignmentOrder
    }

    if (typeof fallback === 'number' && Number.isFinite(fallback))
    {
      return fallback
    }

    return null
  }

  normalizedQuestions.sort((a, b) => {
    const multiA = a.isMultipleChoice ? 0 : 1
    const multiB = b.isMultipleChoice ? 0 : 1
    if (multiA !== multiB)
    {
      return multiA - multiB
    }

    const orderA = resolveQuestionOrder(a.examQuestionId, a.questionNumber)
    const orderB = resolveQuestionOrder(b.examQuestionId, b.questionNumber)

    if (orderA == null && orderB == null)
    {
      return a.examQuestionId - b.examQuestionId
    }

    if (orderA == null)
    {
      return 1
    }

    if (orderB == null)
    {
      return -1
    }

    if (orderA !== orderB)
    {
      return orderA - orderB
    }

    return a.examQuestionId - b.examQuestionId
  })

  const questions = normalizedQuestions

  summary.matchedEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.Matched && entry.isPrimaryMatch && entry.studentSemesterCourseId != null).length
  summary.duplicateEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.Duplicate).length
  summary.unregisteredEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.Unregistered).length
  summary.ambiguousEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.Ambiguous).length
  summary.bookletMismatchEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.BookletMismatch).length
  summary.skippedEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.Skipped).length
  summary.invalidEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.InvalidLine).length

  const matchedEntries = entries.filter(entry => entry.status === OpticalStudentEntryStatus.Matched && entry.isPrimaryMatch && entry.studentSemesterCourseId != null)
  const scoreList = matchedEntries.map(entry => Number(entry.totalPoint ?? 0)).sort((a, b) => a - b)

  if (scoreList.length > 0)
  {
    const sum = scoreList.reduce((acc, value) => acc + value, 0)
    summary.averageScore = roundValue(sum / scoreList.length)

    if (scoreList.length % 2 === 1)
    {
      summary.medianScore = roundValue(scoreList[Math.floor(scoreList.length / 2)])
    }
    else
    {
      const upper = scoreList[scoreList.length / 2]
      const lower = scoreList[(scoreList.length / 2) - 1]
      summary.medianScore = roundValue((upper + lower) / 2)
    }

    const variance = computeVariance(scoreList)
    if (variance > 0)
    {
      summary.standardDeviation = roundValue(Math.sqrt(variance))
    }
  }

  const orderedByScore = matchedEntries
    .map(entry => ({
      id: entry.entryId ?? `${entry.studentSemesterCourseId ?? ''}`,
      score: Number(entry.totalPoint ?? 0),
      reference: entry,
    }))
    .sort((a, b) => b.score - a.score)

  const groupSize = Math.max(1, Math.round(orderedByScore.length * 0.27))
  const safeGroupSize = Math.min(groupSize, orderedByScore.length)

  const topSet = new Set(orderedByScore.slice(0, safeGroupSize).map(item => item.id))
  const bottomSlice = orderedByScore.slice(Math.max(0, orderedByScore.length - safeGroupSize))
  const bottomSet = new Set(bottomSlice.map(item => item.id))

  summary.questionAnalyses = questions.map(question => {
    const normalizedNumber = resolveQuestionOrder(question.examQuestionId, question.questionNumber)
    const analysis: OpticalFormQuestionAnalysis = {
      questionNumber: normalizedNumber != null ? normalizedNumber : question.questionNumber,
      examQuestionId: question.examQuestionId,
      respondentCount: 0,
      correctCount: 0,
      blankCount: 0,
      difficulty: null,
      discrimination: null,
    }

    const baseComment = typeof question.aiReviewComment === 'string' ? question.aiReviewComment.trim() : ''
    analysis.aiReviewComment = baseComment.length > 0 ? baseComment : null
    analysis.aiReviewSignature = question.aiReviewSignature ?? null
    analysis.aiReviewUpdatedAt = question.aiReviewUpdatedAt ?? null
    analysis.aiReviewExamId = typeof question.aiReviewExamId === 'number' ? question.aiReviewExamId : null
    analysis.aiReviewExamQuestionId =
      typeof question.aiReviewExamQuestionId === 'number' ? question.aiReviewExamQuestionId : null

    if (question.isCancelled)
    {
      return analysis
    }

    let topCorrect = 0
    let bottomCorrect = 0

    for (const entry of matchedEntries)
    {
      const detail = entry.answerDetails.find(answer => answer.examQuestionId === question.examQuestionId)
      if (!detail || detail.isCancelled)
      {
        continue
      }

      analysis.respondentCount += 1
      if (!detail.studentOption)
      {
        analysis.blankCount += 1
      }

      if (detail.isCorrect)
      {
        analysis.correctCount += 1
        const entryId = entry.entryId ?? `${entry.studentSemesterCourseId ?? ''}`
        if (topSet.has(entryId))
        {
          topCorrect += 1
        }
        if (bottomSet.has(entryId))
        {
          bottomCorrect += 1
        }
      }
    }

    if (analysis.respondentCount > 0)
    {
      analysis.difficulty = roundValue(analysis.correctCount / analysis.respondentCount, 4)
    }

    if (safeGroupSize > 0 && analysis.respondentCount > 0)
    {
      analysis.discrimination = roundValue((topCorrect / safeGroupSize) - (bottomCorrect / safeGroupSize), 4)
    }

    return analysis
  })

  const activeQuestions = questions.filter(question => !question.isCancelled)

  const outcomeAggregates = new Map<string, LearningOutcomeAggregate>()
  const addAggregate = (
    key: string,
    type: string,
    outcomeId: number,
    outcomeName: string | null | undefined,
    order: number | null | undefined,
    questionId: number | null | undefined
  ) => {
    if (!Number.isFinite(outcomeId) || outcomeId <= 0)
    {
      return
    }

    let aggregate = outcomeAggregates.get(key)
    if (!aggregate)
    {
      aggregate = {
        type,
        outcomeId,
        outcomeName: typeof outcomeName === 'string' ? outcomeName.trim() : '',
        order: typeof order === 'number' ? order : null,
        questionIds: new Set<number>(),
        cronbachAlpha: null,
        kr20: null,
      }
      outcomeAggregates.set(key, aggregate)
    }
    else
    {
      if (
        (!aggregate.outcomeName || aggregate.outcomeName.trim().length === 0) &&
        typeof outcomeName === 'string' &&
        outcomeName.trim().length > 0
      )
      {
        aggregate.outcomeName = outcomeName.trim()
      }

      if (typeof order === 'number')
      {
        if (aggregate.order == null || order < aggregate.order)
        {
          aggregate.order = order
        }
      }
    }

    if (typeof questionId === 'number' && Number.isFinite(questionId))
    {
      aggregate.questionIds.add(questionId)
    }
  }

  for (const question of activeQuestions)
  {
    if (Array.isArray(question.learningOutcomes) && question.learningOutcomes.length > 0)
    {
      for (const outcome of question.learningOutcomes)
      {
        if (!outcome || typeof outcome.outcomeId !== 'number' || outcome.outcomeId <= 0)
        {
          continue
        }

        const normalizedType = typeof outcome.type === 'string' && outcome.type.trim().length > 0
          ? outcome.type
          : 'CourseOutcome'
        const aggregateKey = `${normalizedType}:${outcome.outcomeId}`

        addAggregate(
          aggregateKey,
          normalizedType,
          outcome.outcomeId,
          outcome.outcomeName,
          typeof outcome.order === 'number' ? outcome.order : null,
          question.examQuestionId
        )
      }
    }

    const assignment = questionAssignments?.[question.examQuestionId]
    if (assignment)
    {
      const normalizedGroupType = typeof assignment.groupType === 'string'
        ? assignment.groupType.trim().toLowerCase()
        : ''
      const groupId = typeof assignment.groupId === 'number' ? assignment.groupId : null
      const groupName = typeof assignment.groupName === 'string' ? assignment.groupName : null

      if (
        normalizedGroupType === 'department' &&
        typeof groupId === 'number' &&
        Number.isFinite(groupId) &&
        groupId > 0
      )
      {
        const aggregateType = 'Department'
        addAggregate(
          `${aggregateType}:${groupId}`,
          aggregateType,
          groupId,
          groupName,
          null,
          question.examQuestionId
        )
      }
      else if (
        normalizedGroupType === 'outcome' &&
        typeof groupId === 'number' &&
        Number.isFinite(groupId) &&
        groupId > 0
      )
      {
        const aggregateType = 'AssignmentOutcome'
        addAggregate(
          `${aggregateType}:${groupId}`,
          aggregateType,
          groupId,
          groupName,
          null,
          question.examQuestionId
        )
      }
    }
  }

  if (activeQuestions.length > 1 && matchedEntries.length > 1)
  {
    const perQuestionScores = new Map<number, number[]>()
    for (const question of activeQuestions)
    {
      perQuestionScores.set(question.examQuestionId, [])
    }

    const totalScores: number[] = []

    for (const entry of matchedEntries)
    {
      let entryScore = 0
      for (const question of activeQuestions)
      {
        const detail = entry.answerDetails.find(answer => answer.examQuestionId === question.examQuestionId)

        const score = detail && detail.isCorrect && !detail.isCancelled ? 1 : 0

        perQuestionScores.get(question.examQuestionId)!.push(score)
        entryScore += score
      }
      totalScores.push(entryScore)
    }

    summary.cronbachAlpha = null
    summary.cronbachAlpha01 = null
    summary.kr20 = null

    const computeKr20Value = (values: number[][], totalVar: number, questionCount: number): number | null => {
      if (!Array.isArray(values) || values.length === 0 || !Number.isFinite(totalVar) || totalVar <= 0 || questionCount <= 1)
      {
        return null
      }

      let pqSum = 0
      let hasData = false

      for (const scores of values)
      {
        if (!Array.isArray(scores) || scores.length === 0)
        {
          continue
        }

        hasData = true

        let correctCount = 0
        for (let i = 0; i < scores.length; i += 1)
        {
          const value = Number(scores[i] ?? 0)
          if (Number.isFinite(value))
          {
            correctCount += value
          }
        }

        const responseCount = scores.length
        if (responseCount === 0)
        {
          continue
        }

        const p = correctCount / responseCount
        const q = 1 - p
        pqSum += p * q
      }

      if (!hasData)
      {
        return null
      }

      const rawValue = (questionCount / (questionCount - 1)) * (1 - (pqSum / totalVar))
      return Number.isFinite(rawValue) ? rawValue : null
    }

    const totalVariance = computeVariance(totalScores)
    if (totalVariance > 0)
    {
      let sumVariances = 0
      perQuestionScores.forEach(scores => {
        sumVariances += computeVariance(scores)
      })

      const questionCount = activeQuestions.length
      if (questionCount > 1)
      {
      const alpha = (questionCount / (questionCount - 1)) * (1 - (sumVariances / totalVariance))
      const roundedAlpha = Number.isFinite(alpha) ? roundValue(alpha, 4) : null
      summary.cronbachAlpha = roundedAlpha
      summary.cronbachAlpha01 = roundedAlpha

      const kr20Value = computeKr20Value(Array.from(perQuestionScores.values()), totalVariance, questionCount)
      summary.kr20 = kr20Value != null ? roundValue(kr20Value, 4) : null

        const studentCount = totalScores.length
        const itemIds = activeQuestions.map(question => question.examQuestionId)
        const itemScoreVectors: Record<number, number[]> = {}
        const itemVariances: Record<number, number> = {}

        itemIds.forEach(questionId => {
          const values = perQuestionScores.get(questionId) || []
          itemScoreVectors[questionId] = values
          itemVariances[questionId] = computeVariance(values)
        })

        const totalVarianceByItem: Record<number, number> = {}
        const totalScoresMinusItem: Record<number, number[]> = {}

        itemIds.forEach(questionId => {
          const scores = itemScoreVectors[questionId]
          const minusScores = new Array(studentCount)
          for (let i = 0; i < studentCount; i++)
          {
            minusScores[i] = totalScores[i] - scores[i]
          }
          totalScoresMinusItem[questionId] = minusScores
          totalVarianceByItem[questionId] = computeVariance(minusScores)
        })

        const alphaIfDeleted: Record<number, number | null> = {}
        const itemRestCorrelations: Record<number, number | null> = {}

        itemIds.forEach(questionId => {
          if (questionCount <= 2)
          {
            alphaIfDeleted[questionId] = null
          }
          else
          {
            const itemVariance = itemVariances[questionId]
            const sumVarianceMinusItem = sumVariances - itemVariance
            const varianceMinusItem = totalVarianceByItem[questionId]

            if (varianceMinusItem > 0)
            {
              const alphaValue = ((questionCount - 1) / (questionCount - 2)) * (1 - (sumVarianceMinusItem / varianceMinusItem))
              alphaIfDeleted[questionId] = roundValue(alphaValue, 4)
            }
            else
            {
              alphaIfDeleted[questionId] = null
            }
          }

          const correlation = computeCorrelation(itemScoreVectors[questionId], totalScoresMinusItem[questionId])
          itemRestCorrelations[questionId] = Number.isFinite(correlation) ? roundValue(correlation, 4) : null
        })

        const analysisById = new Map<number, OpticalFormQuestionAnalysis>()
        summary.questionAnalyses.forEach(questionAnalysis => {
          analysisById.set(questionAnalysis.examQuestionId, questionAnalysis)
        })

        itemIds.forEach(questionId => {
          const analysis = analysisById.get(questionId)
          if (!analysis)
          {
            return
          }

          const alphaIfRemoved = alphaIfDeleted[questionId]
          analysis.alphaIfDeleted = alphaIfRemoved ?? null
          analysis.alphaDelta = alphaIfRemoved != null && summary.cronbachAlpha != null
            ? roundValue(alphaIfRemoved - summary.cronbachAlpha, 4)
            : null
          analysis.itemRestCorrelation = itemRestCorrelations[questionId] ?? null
        })

        outcomeAggregates.forEach(aggregate => {
          const questionIds = Array.from(aggregate.questionIds)
          if (questionIds.length <= 1)
          {
            aggregate.cronbachAlpha = null
            aggregate.kr20 = null
            return
          }

          const outcomeTotals: number[] = []
          for (let entryIndex = 0; entryIndex < matchedEntries.length; entryIndex += 1)
          {
            let entrySum = 0
            for (const questionId of questionIds)
            {
              const scores = perQuestionScores.get(questionId)
              if (!scores)
              {
                continue
              }

              const score = entryIndex < scores.length ? scores[entryIndex] ?? 0 : 0
              entrySum += Number.isFinite(score) ? score : 0
            }

            outcomeTotals.push(entrySum)
          }

          const outcomeVariance = computeVariance(outcomeTotals)
          if (outcomeVariance <= 0)
          {
            aggregate.cronbachAlpha = null
            aggregate.kr20 = null
            return
          }

          let outcomeSumVariances = 0
          const outcomeQuestionScores: number[][] = []
          for (const questionId of questionIds)
          {
            const scores = perQuestionScores.get(questionId)
            if (!scores)
            {
              continue
            }

            outcomeSumVariances += computeVariance(scores)
            outcomeQuestionScores.push(scores)
          }

          const outcomeQuestionCount = questionIds.length
          if (outcomeQuestionCount <= 1)
          {
            aggregate.cronbachAlpha = null
            aggregate.kr20 = null
            return
          }

          const alphaValue =
            (outcomeQuestionCount / (outcomeQuestionCount - 1)) *
            (1 - (outcomeSumVariances / outcomeVariance))

          const outcomeAlpha = Number.isFinite(alphaValue) ? roundValue(alphaValue, 4) : null
          aggregate.cronbachAlpha = outcomeAlpha

          const outcomeKr20 = computeKr20Value(outcomeQuestionScores, outcomeVariance, outcomeQuestionCount)
          aggregate.kr20 = outcomeKr20 != null ? roundValue(outcomeKr20, 4) : null
        })
      }
    }
  }

  summary.learningOutcomeReliabilities = Array.from(outcomeAggregates.values())
    .map(aggregate => ({
      type: aggregate.type,
      outcomeId: aggregate.outcomeId,
      outcomeName: aggregate.outcomeName,
      questionCount: aggregate.questionIds.size,
      cronbachAlpha: aggregate.cronbachAlpha,
      cronbachAlpha01: aggregate.cronbachAlpha,
      kr20: aggregate.kr20,
      order: aggregate.order,
    }))
    .sort((a, b) => {
      const typeCompare = a.type.localeCompare(b.type, undefined, {sensitivity: 'base'})
      if (typeCompare !== 0)
      {
        return typeCompare
      }

      const orderA = typeof a.order === 'number' ? a.order : Number.POSITIVE_INFINITY
      const orderB = typeof b.order === 'number' ? b.order : Number.POSITIVE_INFINITY
      if (orderA !== orderB)
      {
        return orderA - orderB
      }

      const nameA = a.outcomeName?.trim() ?? ''
      const nameB = b.outcomeName?.trim() ?? ''
      return nameA.localeCompare(nameB, undefined, {sensitivity: 'base'})
    })

  return summary
}

const recalculateExamWithConfiguration = (
  exam: OpticalFormExamResult,
  config: ExamConfiguration,
  questionAssignments?: ExamQuestionAssignmentMap | null
): OpticalFormExamResult => {
  const questionConfigMap = { ...config.questions }
  const configsArray = Object.values(questionConfigMap)
  const scalingFactor = computeScalingFactor(configsArray)
  const averagePenaltyPoint = computeAverageMultipleChoicePoint(configsArray)

  const effectivePointMap: Record<number, number> = {}

  const computeEffectivePoint = (
    examQuestionId: number,
    basePoint: number,
    isCancelled: boolean
  ): number => {
    const normalizedBase = Number.isFinite(basePoint) ? basePoint : 0
    const effective = isCancelled ? 0 : roundValue(normalizedBase * scalingFactor)
    effectivePointMap[examQuestionId] = effective
    return effective
  }

  const updatedQuestions = exam.questions.map(question => {
    const questionConfig = questionConfigMap[question.examQuestionId]
    const basePoint = Number.isFinite(questionConfig?.point)
      ? questionConfig!.point
      : typeof question.point === 'number'
        ? question.point
        : 0
    const isCancelled = questionConfig ? questionConfig.isCancelled : Boolean(question.isCancelled)
    const effectivePoint = computeEffectivePoint(question.examQuestionId, basePoint, isCancelled)
    return {
      ...question,
      acceptedOptions: questionConfig
        ? getAcceptedOptionsForBooklet(questionConfig, config.masterBookletCode, config.masterBookletCode)
        : Array.isArray(question.acceptedOptions)
          ? normalizeAcceptedOptionsList(question.acceptedOptions)
          : [],
      isCancelled,
      isMultipleChoice: questionConfig ? questionConfig.isMultipleChoice : Boolean(question.isMultipleChoice),
      point: Number.isFinite(basePoint) ? roundValue(basePoint) : basePoint,
      effectivePoint,
    }
  })

  const updatedEntries = exam.entries.map(entry =>
    recalculateEntry(entry, questionConfigMap, scalingFactor, config.penalty, averagePenaltyPoint, config.masterBookletCode)
  )

  const updatedBooklets = Array.isArray(exam.booklets)
    ? exam.booklets.map(booklet => {
        const normalizedCode = normalizeBookletCode(booklet.code)
        const updatedBookletQuestions = Array.isArray(booklet.questions)
          ? booklet.questions.map(question => {
              const questionConfig = questionConfigMap[question.examQuestionId]
              const acceptedOptions = questionConfig
                ? getAcceptedOptionsForBooklet(questionConfig, normalizedCode, config.masterBookletCode)
                : normalizeAcceptedOptionsList(question.acceptedOptions)
              const basePoint = Number.isFinite(questionConfig?.point)
                ? questionConfig!.point
                : typeof question.point === 'number'
                  ? question.point
                  : 0
              const isCancelled = questionConfig ? questionConfig.isCancelled : false
              const effectivePoint =
                effectivePointMap[question.examQuestionId] ??
                computeEffectivePoint(question.examQuestionId, basePoint, isCancelled)

              return {
                ...question,
                acceptedOptions,
                point: Number.isFinite(basePoint) ? roundValue(basePoint) : basePoint,
                effectivePoint,
              }
            })
          : []

        return {
          ...booklet,
          questions: updatedBookletQuestions,
        }
      })
    : []

  const missingStudents = recomputeMissingStudents(exam.students, updatedEntries)
  const summary = buildSummaryFromEntries(updatedEntries, updatedQuestions, questionAssignments)

  return {
    ...exam,
    questions: updatedQuestions,
    entries: updatedEntries,
    missingStudents,
    booklets: updatedBooklets,
    summary,
    effectiveQuestionPoints: effectivePointMap,
    questionScalingFactor: Number.isFinite(scalingFactor) ? roundValue(scalingFactor, 4) : scalingFactor,
  }
}

const statusBadgeClass: Record<OpticalStudentEntryStatus, string> = {
  [OpticalStudentEntryStatus.Matched]: 'badge-light-success',
  [OpticalStudentEntryStatus.Duplicate]: 'badge-light-warning',
  [OpticalStudentEntryStatus.Unregistered]: 'badge-light-danger',
  [OpticalStudentEntryStatus.BookletMismatch]: 'badge-light-info',
  [OpticalStudentEntryStatus.Ambiguous]: 'badge-light-primary',
  [OpticalStudentEntryStatus.InvalidLine]: 'badge-light-dark',
  [OpticalStudentEntryStatus.Skipped]: 'badge-light-secondary',
}

const statusLabels: Record<OpticalStudentEntryStatus, string> = {
  [OpticalStudentEntryStatus.Matched]: 'Eşleşti',
  [OpticalStudentEntryStatus.Duplicate]: 'Mükerrer',
  [OpticalStudentEntryStatus.Unregistered]: 'Kayıtsız Öğrenci',
  [OpticalStudentEntryStatus.BookletMismatch]: 'Kitapçık Uyuşmuyor',
  [OpticalStudentEntryStatus.Ambiguous]: 'Birden Fazla Ders',
  [OpticalStudentEntryStatus.InvalidLine]: 'Geçersiz Satır',
  [OpticalStudentEntryStatus.Skipped]: 'Yoksayıldı',
}

const examTypeLabels: Record<string, string> = {
  A: 'Ara Sınav',
  F: 'Final',
  B: 'Bütünleme',
}

const ExamOpticalReadingPage: React.FC = () => {
  const [examPeriods, setExamPeriods] = useState<ExamPeriodOption[]>([])
  const [selectedExamPeriod, setSelectedExamPeriod] = useState<number | null>(null)
  const [examOptions, setExamOptions] = useState<ExamOption[]>([])
  const [isExamOptionsLoading, setIsExamOptionsLoading] = useState(false)
  const [selectedExamIds, setSelectedExamIds] = useState<number[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [examResults, setExamResults] = useState<OpticalFormExamResult[]>([])
  const [examConfigurations, setExamConfigurations] = useState<Record<number, ExamConfiguration>>({})
  const [questionAssignments, setQuestionAssignments] = useState<ExamAssignmentsState>({})
  const [optionMappings, setOptionMappings] = useState<ExamOptionMappings>({})
  const [bookletQuestionOrders, setBookletQuestionOrders] = useState<ExamBookletOrdersState>({})
  const [processErrors, setProcessErrors] = useState<string[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [expandedBooklets, setExpandedBooklets] = useState<Record<number, string | null>>({})
  const [selectedEntryDetail, setSelectedEntryDetail] = useState<{examId: number; entryId: string} | null>(null)
  const [questionPreviewContext, setQuestionPreviewContext] = useState<QuestionPreviewContext | null>(null)
  const [questionReviewCache, setQuestionReviewCache] = useState<Record<string, QuestionReviewCacheEntry>>({})
  const [acceptedOptionsInputCache, setAcceptedOptionsInputCache] = useState<
    Record<number, Record<number, string>>
  >({})
  const [bookletMappingModal, setBookletMappingModal] = useState<BookletMappingModalState | null>(null)
  const [draggedQuestion, setDraggedQuestion] = useState<{code: string; index: number} | null>(null)

  const examGroupsByBaseId = useMemo(() => {
    const groups: Record<number, ExamOption[]> = {}
    examOptions.forEach(option => {
      if (!option || typeof option.id !== 'number' || !Number.isFinite(option.id)) {
        return
      }
      const referenceId =
        typeof option.referenceId === 'number' && Number.isFinite(option.referenceId)
          ? option.referenceId
          : null
      const baseExamId =
        referenceId && referenceId > 0
          ? referenceId
          : typeof option.baseExamId === 'number' && option.baseExamId > 0
          ? option.baseExamId
          : option.id

      if (!groups[baseExamId]) {
        groups[baseExamId] = []
      }

      groups[baseExamId].push(option)
    })

    return groups
  }, [examOptions])

  const getBaseExamId = useCallback(
    (examId: number): number => {
      const exam = examOptions.find(option => option && option.id === examId)
      if (!exam) {
        return examId
      }

      const referenceId =
        typeof exam.referenceId === 'number' && Number.isFinite(exam.referenceId)
          ? exam.referenceId
          : null

      if (referenceId && referenceId > 0) {
        return referenceId
      }

      if (typeof exam.baseExamId === 'number' && Number.isFinite(exam.baseExamId) && exam.baseExamId > 0) {
        return exam.baseExamId
      }

      return exam.id
    },
    [examOptions]
  )

  const getGroupExamIds = useCallback(
    (baseExamId: number): number[] => {
      if (typeof baseExamId !== 'number' || !Number.isFinite(baseExamId)) {
        return []
      }

      const group = examGroupsByBaseId[baseExamId]

      if (Array.isArray(group) && group.length > 0) {
        return group
          .map(option => option?.id)
          .filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
      }

      const fallback = examOptions.find(option => option && option.id === baseExamId)

      return fallback ? [fallback.id] : []
    },
    [examGroupsByBaseId, examOptions]
  )

  const alignExamBookletsWithBaseExam = useCallback(
    (exams: OpticalFormExamResult[]): OpticalFormExamResult[] => {
      if (!Array.isArray(exams) || exams.length === 0) {
        return []
      }

      const examToBaseId = new Map<number, number>()
      const baseExamMap = new Map<number, OpticalFormExamResult>()

      exams.forEach(exam => {
        const resolvedBaseId = getBaseExamId(exam.examId)
        if (!Number.isFinite(resolvedBaseId)) {
          return
        }

        examToBaseId.set(exam.examId, resolvedBaseId)

        const existing = baseExamMap.get(resolvedBaseId)
        if (!existing || exam.examId === resolvedBaseId) {
          baseExamMap.set(resolvedBaseId, exam)
        }
      })

      return exams.map(exam => {
        const baseId = examToBaseId.get(exam.examId)
        if (!baseId) {
          return exam
        }

        const baseExam = baseExamMap.get(baseId)
        if (!baseExam || baseExam.examId === exam.examId) {
          return exam
        }

        return {
          ...exam,
          booklets: cloneExamBooklets(baseExam.booklets),
          masterBookletCode: baseExam.masterBookletCode ?? exam.masterBookletCode,
        }
      })
    },
    [getBaseExamId]
  )

  const examResultsRef = useRef<OpticalFormExamResult[]>(examResults)
  const examConfigurationsRef = useRef<Record<number, ExamConfiguration>>(examConfigurations)
  const selectedExamPeriodRef = useRef<number | null>(selectedExamPeriod)
  const questionAssignmentsRef = useRef<ExamAssignmentsState>(questionAssignments)
  const optionMappingsRef = useRef<ExamOptionMappings>(optionMappings)
  const bookletQuestionOrdersRef = useRef<ExamBookletOrdersState>(bookletQuestionOrders)

  useEffect(() => {
    examResultsRef.current = examResults
  }, [examResults])

  useEffect(() => {
    examConfigurationsRef.current = examConfigurations
  }, [examConfigurations])

  useEffect(() => {
    selectedExamPeriodRef.current = selectedExamPeriod
  }, [selectedExamPeriod])

  useEffect(() => {
    questionAssignmentsRef.current = questionAssignments
  }, [questionAssignments])

  useEffect(() => {
    optionMappingsRef.current = optionMappings
  }, [optionMappings])

  useEffect(() => {
    bookletQuestionOrdersRef.current = bookletQuestionOrders
  }, [bookletQuestionOrders])

  const buildBaseQuestionOrder = useCallback(
    (examId: number): number[] => {
      const assignments = questionAssignmentsRef.current[examId]
      if (assignments) {
        return Object.values(assignments)
          .filter(assignment => typeof assignment?.examQuestionId === 'number')
          .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
          .map(assignment => assignment.examQuestionId)
      }

      const exam = examResultsRef.current.find(item => item.examId === examId)
      if (exam && Array.isArray(exam.questions)) {
        return exam.questions
          .filter(question => typeof question?.examQuestionId === 'number')
          .sort((a, b) => (Number(a?.questionNumber ?? 0) - Number(b?.questionNumber ?? 0)))
          .map(question => question.examQuestionId)
      }

      return []
    },
    []
  )

  const convertOrderListToMap = useCallback((orders: BookletOrderListState): ExamBookletQuestionOrders => {
    const result: ExamBookletQuestionOrders = {}

    Object.entries(orders).forEach(([code, list]) => {
      const normalized = normalizeBookletCode(code)
      if (!normalized) {
        return
      }

      const mapping: BookletQuestionOrderMap = {}
      list.forEach((questionId, index) => {
        if (typeof questionId === 'number' && Number.isFinite(questionId)) {
          mapping[questionId] = index + 1
        }
      })

      result[normalized] = mapping
    })

    return result
  }, [])

  const convertOptionOrdersToPayload = useCallback(
    (optionOrders: BookletOptionOrdersState): Record<number, Record<string, number[]>> => {
      const payload: Record<number, Record<string, number[]>> = {}

      Object.entries(optionOrders).forEach(([code, questionMap]) => {
        const normalizedCode = normalizeBookletCode(code)
        if (!normalizedCode) {
          return
        }

        Object.entries(questionMap ?? {}).forEach(([questionIdKey, order]) => {
          const questionId = Number(questionIdKey)
          if (!Number.isFinite(questionId)) {
            return
          }

          const cleanedOrder = Array.isArray(order)
            ? order.filter(item => typeof item === 'number' && Number.isFinite(item))
            : []

          if (!payload[questionId]) {
            payload[questionId] = {}
          }

          payload[questionId][normalizedCode] = Array.from(new Set(cleanedOrder))
        })
      })

      return payload
    },
    []
  )

  const applyMappingResponse = useCallback(
    (
      response: ExamBookletMappingResponse,
      examId: number,
      exam?: OpticalFormExamResult
    ): {orders: BookletOrderListState; optionOrders: BookletOptionOrdersState} => {
      const baseOrder = buildBaseQuestionOrder(examId)
      const resolvedExam = exam ?? examResultsRef.current.find(item => item.examId === examId) ?? null
      const examContext: OpticalFormExamResult =
        resolvedExam ?? {
          examId,
          examName: '',
          courseName: '',
          courseCode: '',
          examType: '',
          semesterName: '',
          questionCount: 0,
          questions: [],
          students: [],
          entries: [],
          missingStudents: [],
          booklets: [],
          masterBookletCode: '',
          summary: null,
          effectiveQuestionPoints: {},
          questionScalingFactor: null,
        }
      const orders: BookletOrderListState = {}
      const optionOrders: BookletOptionOrdersState = {}
      const allowedCodes = new Set<string>()

      if (Array.isArray(response?.bookletCodes)) {
        response.bookletCodes.forEach(code => {
          const normalized = normalizeBookletCode(code)
          if (normalized) {
            allowedCodes.add(normalized)
          }
        })
      }

      const sourceOrders = response?.bookletQuestionOrder ?? {}
      Object.entries(sourceOrders).forEach(([code, list]) => {
        const normalized = normalizeBookletCode(code)
        if (!normalized) {
          return
        }

        if (allowedCodes.size > 0 && !allowedCodes.has(normalized)) {
          return
        }

        const cleanedList = Array.isArray(list)
          ? list
              .map(item => (typeof item === 'number' ? item : Number(item)))
              .filter(item => typeof item === 'number' && Number.isFinite(item))
          : []

        orders[normalized] = cleanedList.length > 0 ? Array.from(new Set(cleanedList)) : [...baseOrder]
      })

      if (allowedCodes.size > 0) {
        allowedCodes.forEach(code => {
          if (!orders[code]) {
            orders[code] = [...baseOrder]
          }
        })
      }

      if (Object.keys(orders).length === 0 && baseOrder.length > 0) {
        const fallbackCode = normalizeBookletCode(resolvedExam?.masterBookletCode) ?? MASTER_BOOKLET_CODE
        orders[fallbackCode] = [...baseOrder]
      }

      const mappedOrders = convertOrderListToMap(orders)
      setBookletQuestionOrders(prev => ({...prev, [examId]: mappedOrders}))

      const optionPayload = response?.optionOrderByBooklet ?? {}
      const assignmentMap = questionAssignmentsRef.current[examId] ?? {}
      const optionMappingForExam: ExamQuestionOptionMappings = {
        ...(optionMappingsRef.current[examId] ?? {}),
      }
      const updatedAssignments: ExamQuestionAssignmentMap = {...assignmentMap}
      const masterCode = normalizeBookletCode(resolvedExam?.masterBookletCode) ?? MASTER_BOOKLET_CODE

      Object.entries(optionPayload).forEach(([questionIdKey, variantMap]) => {
        const questionId = Number(questionIdKey)
        if (!Number.isFinite(questionId)) {
          return
        }

        const assignment = updatedAssignments[questionId]
        if (!assignment) {
          return
        }

        const normalizedVariants: Record<string, number[]> = {}
        Object.entries(variantMap ?? {}).forEach(([code, order]) => {
          const normalized = normalizeBookletCode(code)
          if (!normalized) {
            return
          }

          const cleanedOrder = Array.isArray(order)
            ? order
                .map(item => (typeof item === 'number' ? item : Number(item)))
                .filter(item => Number.isFinite(item))
            : []

          if (cleanedOrder.length > 0) {
            normalizedVariants[normalized] = Array.from(new Set(cleanedOrder))
          }
        })

        if (Object.keys(normalizedVariants).length === 0) {
          return
        }

        assignment.optionOrderByBooklet = {
          ...(assignment.optionOrderByBooklet ?? {}),
          ...normalizedVariants,
        }

        const defaultOrder = normalizedVariants[masterCode]
        if (defaultOrder && defaultOrder.length > 0) {
          assignment.optionOrder = [...defaultOrder]
        }

        updatedAssignments[questionId] = {...assignment}
        optionMappingForExam[questionId] = buildQuestionOptionMappings(updatedAssignments[questionId], examContext)
      })

      questionAssignmentsRef.current = {...questionAssignmentsRef.current, [examId]: updatedAssignments}
      setQuestionAssignments(prev => ({...prev, [examId]: updatedAssignments}))
      optionMappingsRef.current = {...optionMappingsRef.current, [examId]: optionMappingForExam}
      setOptionMappings(prev => ({...prev, [examId]: optionMappingForExam}))

      Object.entries(orders).forEach(([code, questionList]) => {
        const normalizedCode = normalizeBookletCode(code)
        if (!normalizedCode) {
          return
        }

        const perQuestion: Record<number, number[]> = {}
        questionList.forEach(questionId => {
          const variantSource = optionPayload?.[questionId]
          const candidate = variantSource?.[normalizedCode] ?? variantSource?.[masterCode]
          const cleanedCandidate = Array.isArray(candidate)
            ? candidate
                .map(item => (typeof item === 'number' ? item : Number(item)))
                .filter(item => Number.isFinite(item))
            : []

          if (cleanedCandidate.length > 0) {
            perQuestion[questionId] = Array.from(new Set(cleanedCandidate))
          } else {
            const assignment = updatedAssignments[questionId]
            const fallbackOrder = assignment?.optionOrderByBooklet?.[normalizedCode]
              ?? assignment?.optionOrder
              ?? (assignment?.options ?? [])
                    .map(option => (typeof option?.id === 'number' ? (option.id as number) : null))
                    .filter((id): id is number => Number.isFinite(id))
            perQuestion[questionId] = Array.from(new Set(fallbackOrder ?? []))
          }
        })

        optionOrders[normalizedCode] = perQuestion
      })

      return {orders, optionOrders}
    },
    [buildBaseQuestionOrder, convertOrderListToMap, setBookletQuestionOrders]
  )

  const buildInitialBookletOrders = useCallback(
    (exam: OpticalFormExamResult): BookletOrderListState => {
      const existing = bookletQuestionOrdersRef.current[exam.examId] ?? {}
      const baseOrder = buildBaseQuestionOrder(exam.examId)
      const normalizedMaster = normalizeBookletCode(exam.masterBookletCode)
      const codes = new Set<string>()

      Object.keys(existing).forEach(code => {
        const normalized = normalizeBookletCode(code)
        if (normalized) {
          codes.add(normalized)
        }
      })

      if (Array.isArray(exam.booklets)) {
        exam.booklets.forEach(booklet => {
          const normalized = normalizeBookletCode(booklet.code)
          if (normalized) {
            codes.add(normalized)
          }
        })
      }

      if (normalizedMaster) {
        codes.add(normalizedMaster)
      }

      const orders: BookletOrderListState = {}

      if (codes.size === 0 && baseOrder.length > 0) {
        codes.add(normalizedMaster ?? MASTER_BOOKLET_CODE)
      }

      Array.from(codes).forEach(code => {
        const mapping = existing[code] ?? (code === normalizedMaster ? existing[MASTER_BOOKLET_CODE] : undefined)
        if (mapping) {
          const orderedList = Object.entries(mapping)
            .map(([questionId, order]) => ({
              questionId: typeof questionId === 'number' ? questionId : Number(questionId),
              orderValue: typeof order === 'number' ? order : Number(order),
            }))
            .filter(item => Number.isFinite(item.questionId) && Number.isFinite(item.orderValue))
            .sort((a, b) => a.orderValue - b.orderValue)
            .map(item => item.questionId)

          orders[code] = orderedList.length > 0 ? orderedList : [...baseOrder]
        } else {
          orders[code] = [...baseOrder]
        }
      })

      return orders
    },
    [buildBaseQuestionOrder]
  )

  const buildInitialBookletOptionOrders = useCallback(
    (exam: OpticalFormExamResult, orders: BookletOrderListState): BookletOptionOrdersState => {
      const assignmentMap = questionAssignmentsRef.current[exam.examId] ?? {}
      const optionOrders: BookletOptionOrdersState = {}

      Object.entries(orders).forEach(([code, questionList]) => {
        const normalizedCode = normalizeBookletCode(code)
        if (!normalizedCode) {
          return
        }

        const perQuestion: Record<number, number[]> = {}
        questionList.forEach(questionId => {
          const assignment = assignmentMap[questionId]
          if (!assignment) {
            return
          }

          const variantMap = assignment.optionOrderByBooklet ?? {}
          const candidate = variantMap[normalizedCode]
            ?? variantMap[MASTER_BOOKLET_CODE]
            ?? assignment.optionOrder
            ?? (assignment.options ?? [])
                  .map(option => (typeof option?.id === 'number' ? (option.id as number) : null))
                  .filter((id): id is number => Number.isFinite(id))

          perQuestion[questionId] = Array.from(new Set(candidate ?? []))
        })

        optionOrders[normalizedCode] = perQuestion
      })

      return optionOrders
    },
    []
  )

  const openBookletMappingModal = useCallback(
    (exam: OpticalFormExamResult) => {
      const orders = buildInitialBookletOrders(exam)
      const codes = Object.keys(orders)
      const optionOrders = buildInitialBookletOptionOrders(exam, orders)
      const firstQuestion = codes.length > 0 && orders[codes[0]] ? orders[codes[0]][0] ?? null : null
      setDraggedQuestion(null)
      setBookletMappingModal({
        exam,
        orders,
        activeCode: codes.length > 0 ? codes[0] : null,
        optionOrders,
        selectedQuestionId: firstQuestion,
        uploadFile: null,
        isSaving: false,
        isImporting: false,
      })
    },
    [buildInitialBookletOptionOrders, buildInitialBookletOrders]
  )

  const closeBookletMappingModal = useCallback(() => {
    setDraggedQuestion(null)
    setBookletMappingModal(null)
  }, [])

  const moveBookletQuestion = useCallback((code: string, fromIndex: number, toIndex: number) => {
    setBookletMappingModal(prev => {
      if (!prev || !prev.orders[code]) {
        return prev
      }

      const list = [...prev.orders[code]]
      if (fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length) {
        return prev
      }

      const [moved] = list.splice(fromIndex, 1)
      list.splice(toIndex, 0, moved)

      return {
        ...prev,
        orders: {
          ...prev.orders,
          [code]: list,
        },
      }
    })
  }, [])

  const moveBookletOption = useCallback((code: string, questionId: number, fromIndex: number, toIndex: number) => {
    setBookletMappingModal(prev => {
      if (!prev || !prev.optionOrders[code]) {
        return prev
      }

      const currentOrder = prev.optionOrders[code][questionId] ?? []
      if (!Array.isArray(currentOrder) || fromIndex === toIndex) {
        return prev
      }

      if (
        fromIndex < 0 ||
        fromIndex >= currentOrder.length ||
        toIndex < 0 ||
        toIndex >= currentOrder.length
      ) {
        return prev
      }

      const reordered = [...currentOrder]
      const [moved] = reordered.splice(fromIndex, 1)
      if (typeof moved !== 'number') {
        return prev
      }
      reordered.splice(toIndex, 0, moved)

      return {
        ...prev,
        optionOrders: {
          ...prev.optionOrders,
          [code]: {
            ...(prev.optionOrders[code] ?? {}),
            [questionId]: reordered,
          },
        },
      }
    })
  }, [])

  const handleBookletDragStart = useCallback((code: string, index: number) => {
    setDraggedQuestion({code, index})
  }, [])

  const handleBookletDrop = useCallback(
    (code: string, index: number) => {
      if (!draggedQuestion || draggedQuestion.code !== code) {
        setDraggedQuestion(null)
        return
      }

      moveBookletQuestion(code, draggedQuestion.index, index)
      setDraggedQuestion(null)
    },
    [draggedQuestion, moveBookletQuestion]
  )

  const handleMappingFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    setBookletMappingModal(prev => (prev ? {...prev, uploadFile: file ?? null} : prev))
  }, [])

  const resolveQuestionLabel = useCallback(
    (examId: number, questionId: number): string => {
      const assignments = questionAssignmentsRef.current[examId]
      const assignment = assignments ? assignments[questionId] : undefined
      if (assignment?.questionName) {
        return assignment.questionName
      }

      const exam = examResultsRef.current.find(item => item.examId === examId)
      const question = exam?.questions.find(item => item.examQuestionId === questionId)
      if (question?.questionNumber != null) {
        return `Soru ${question.questionNumber}`
      }

      if (assignment?.order != null) {
        return `Soru ${assignment.order}`
      }

      return `Soru ${questionId}`
    },
    []
  )

  const handleImportBookletMapping = useCallback(async () => {
    if (!bookletMappingModal?.exam) {
      return
    }

    if (!bookletMappingModal.uploadFile) {
      Swal.fire('Uyarı', 'Lütfen excel dosyasını seçiniz.', 'warning')
      return
    }

      setBookletMappingModal(prev => (prev ? {...prev, isImporting: true} : prev))

      try {
        const response = await importExamBookletMappings(bookletMappingModal.exam.examId, bookletMappingModal.uploadFile)
        const {orders: updatedOrders, optionOrders} = applyMappingResponse(
          response,
          bookletMappingModal.exam.examId,
          bookletMappingModal.exam
        )
        setBookletMappingModal(prev =>
          prev
            ? {
                ...prev,
                orders: updatedOrders,
                activeCode: Object.keys(updatedOrders)[0] ?? prev.activeCode,
                optionOrders,
                selectedQuestionId: prev.selectedQuestionId ?? Object.values(updatedOrders)[0]?.[0] ?? null,
                uploadFile: null,
                isImporting: false,
              }
            : prev
        )
      Swal.fire('Başarılı', 'Excel dosyasından kitapçık eşleştirmeleri güncellendi.', 'success')
    } catch (error: any) {
      console.error('Kitapçık eşleştirmeleri içe aktarılırken hata oluştu', error)
      const message =
        error?.response?.data?.message ?? 'Kitapçık eşleştirmeleri içe aktarılırken bir hata oluştu.'
      setBookletMappingModal(prev => (prev ? {...prev, isImporting: false} : prev))
      Swal.fire('Hata', message, 'error')
    }
  }, [applyMappingResponse, bookletMappingModal, convertOptionOrdersToPayload])

  const handleSaveBookletMapping = useCallback(async () => {
    if (!bookletMappingModal?.exam) {
      return
    }

      setBookletMappingModal(prev => (prev ? {...prev, isSaving: true} : prev))

      try {
        const response = await updateExamBookletMappings(
          bookletMappingModal.exam.examId,
          bookletMappingModal.orders,
          convertOptionOrdersToPayload(bookletMappingModal.optionOrders)
        )
        const {orders: updatedOrders, optionOrders} = applyMappingResponse(
          response,
          bookletMappingModal.exam.examId,
          bookletMappingModal.exam
        )
        setBookletMappingModal(prev =>
          prev
            ? {
                ...prev,
                orders: updatedOrders,
                optionOrders,
                activeCode: prev.activeCode ?? Object.keys(updatedOrders)[0] ?? null,
                selectedQuestionId: prev.selectedQuestionId ?? Object.values(updatedOrders)[0]?.[0] ?? null,
                isSaving: false,
              }
            : prev
        )
      Swal.fire('Başarılı', 'Kitapçık eşleştirmeleri kaydedildi.', 'success')
    } catch (error: any) {
      console.error('Kitapçık eşleştirmeleri kaydedilemedi', error)
      const message =
        error?.response?.data?.message ?? 'Kitapçık eşleştirmeleri kaydedilirken bir hata oluştu.'
      setBookletMappingModal(prev => (prev ? {...prev, isSaving: false} : prev))
      Swal.fire('Hata', message, 'error')
    }
  }, [applyMappingResponse, bookletMappingModal])

  useEffect(() => {
    if (!isAiActive) {
      return
    }

    setQuestionReviewCache(prevCache => {
      let hasChanges = false
      const nextCache: Record<string, QuestionReviewCacheEntry> = {...prevCache}

      examResults.forEach(exam => {
        const summary = exam.summary
        if (!summary || !Array.isArray(summary.questionAnalyses)) {
          return
        }

        summary.questionAnalyses.forEach(analysis => {
          const comment = typeof analysis.aiReviewComment === 'string' ? analysis.aiReviewComment.trim() : ''
          if (!comment) {
            return
          }

          const difficultyInsight = resolveDifficultyInsight(analysis)
          const discriminationInsight = resolveDiscriminationInsight(analysis)
          const {cacheKey, signature} = buildQuestionReviewData({
            examId: exam.examId,
            analysis,
            difficultyInsight,
            discriminationInsight,
            questionAssignments: questionAssignmentsRef.current,
            optionMappings: optionMappingsRef.current,
            examConfigurations: examConfigurationsRef.current,
          })

          const existing = nextCache[cacheKey]
          if (existing?.comment && existing.signature === signature) {
            return
          }

          const formattedHtml = formatAiCommentHtml(comment)
          nextCache[cacheKey] = {comment, formattedHtml, signature}
          hasChanges = true
        })
      })

      return hasChanges ? nextCache : prevCache
    })
  }, [examResults, questionAssignments, optionMappings])

  const updateAcceptedOptionsInputCache = useCallback(
    (examId: number, examQuestionId: number, formattedValue: string, canonicalValue: string) => {
      setAcceptedOptionsInputCache(prev => {
        const next = {...prev}
        const examCache = {...(next[examId] ?? {})}

        if (!formattedValue || formattedValue === canonicalValue) {
          delete examCache[examQuestionId]
        } else {
          examCache[examQuestionId] = formattedValue
        }

        if (Object.keys(examCache).length > 0) {
          next[examId] = examCache
        } else {
          delete next[examId]
        }

        return next
      })
    },
    []
  )

  const clearAcceptedOptionsInputCache = useCallback((examId: number, examQuestionId: number) => {
    setAcceptedOptionsInputCache(prev => {
      const current = prev[examId]
      if (!current || typeof current[examQuestionId] === 'undefined') {
        return prev
      }

      const nextExamCache = {...current}
      delete nextExamCache[examQuestionId]

      if (Object.keys(nextExamCache).length === 0) {
        const {[examId]: _removed, ...rest} = prev
        return rest
      }

      return {...prev, [examId]: nextExamCache}
    })
  }, [])

  const loadExamAssignments = useCallback(
    async (exam: OpticalFormExamResult): Promise<void> => {
      if (!exam || !exam.examId) {
        return
      }

      if (questionAssignmentsRef.current[exam.examId]) {
        return
      }

      try {
        const response = await fetchExamAssignments(exam.examId)
        const groups: ExamQuestionAssignmentGroup[] = Array.isArray(response?.groups)
          ? response.groups
          : []

        const assignmentMap: ExamQuestionAssignmentMap = {}
        const questionOptionMappings: ExamQuestionOptionMappings = {}

        groups.forEach(group => {
          const questions: ExamQuestionAssignment[] = Array.isArray(group?.questions)
            ? group.questions
            : []
          questions.forEach(question => {
            if (!question || typeof question.examQuestionId !== 'number') {
              return
            }

            assignmentMap[question.examQuestionId] = question
            questionOptionMappings[question.examQuestionId] = buildQuestionOptionMappings(question, exam)
          })
        })

        const rawBookletOrders = response?.bookletQuestionOrder ?? {}
        const normalizedOrders: ExamBookletQuestionOrders = {}

        if (rawBookletOrders && typeof rawBookletOrders === 'object') {
          Object.entries(rawBookletOrders).forEach(([code, order]) => {
            const normalizedCode = normalizeBookletCode(code)
            if (!normalizedCode || !Array.isArray(order) || order.length === 0) {
              return
            }

            const filtered = order
              .map(item => (typeof item === 'number' ? item : Number(item)))
              .filter((item): item is number => typeof item === 'number' && Number.isFinite(item))

            if (filtered.length === 0) {
              return
            }

            normalizedOrders[normalizedCode] = filtered.reduce<BookletQuestionOrderMap>((acc, examQuestionId, index) => {
              if (typeof examQuestionId === 'number' && Number.isFinite(examQuestionId)) {
                acc[examQuestionId] = index + 1
              }
              return acc
            }, {})
          })
        }

        const normalizedMaster = normalizeBookletCode(exam.masterBookletCode)

        if (normalizedMaster && !normalizedOrders[normalizedMaster]) {
          const orderedAssignments = Object.values(assignmentMap)
            .filter(assignment => typeof assignment?.examQuestionId === 'number')
            .sort((a, b) => (Number(a?.order ?? 0) - Number(b?.order ?? 0)))

          if (orderedAssignments.length > 0) {
            normalizedOrders[normalizedMaster] = orderedAssignments.reduce<BookletQuestionOrderMap>((acc, assignment, index) => {
              const questionId = assignment.examQuestionId
              if (typeof questionId === 'number' && Number.isFinite(questionId)) {
                acc[questionId] = index + 1
              }
              return acc
            }, {})
          }
        }

        if (normalizedMaster && normalizedOrders[normalizedMaster]) {
          normalizedOrders[MASTER_BOOKLET_CODE] = {...normalizedOrders[normalizedMaster]}
        }

        const existingOrders = bookletQuestionOrdersRef.current[exam.examId] ?? {}
        const mergedOrders: ExamBookletQuestionOrders = {...existingOrders}

        Object.entries(normalizedOrders).forEach(([code, mapping]) => {
          mergedOrders[code] = {...mapping}
        })

        questionAssignmentsRef.current = {
          ...questionAssignmentsRef.current,
          [exam.examId]: assignmentMap,
        }
        optionMappingsRef.current = {
          ...optionMappingsRef.current,
          [exam.examId]: questionOptionMappings,
        }
        if (Object.keys(normalizedOrders).length > 0) {
          bookletQuestionOrdersRef.current = {
            ...bookletQuestionOrdersRef.current,
            [exam.examId]: mergedOrders,
          }
        }

        setQuestionAssignments(prev => ({...prev, [exam.examId]: assignmentMap}))
        setOptionMappings(prev => ({...prev, [exam.examId]: questionOptionMappings}))
        if (Object.keys(normalizedOrders).length > 0) {
          setBookletQuestionOrders(prev => ({...prev, [exam.examId]: mergedOrders}))
        }

        setExamResults(prev =>
          prev.map(currentExam => {
            if (currentExam.examId !== exam.examId) {
              return currentExam
            }

            const config = examConfigurationsRef.current[exam.examId]
            if (config) {
              return recalculateExamWithConfiguration(currentExam, config, assignmentMap)
            }

            const summary = buildSummaryFromEntries(
              currentExam.entries,
              currentExam.questions,
              assignmentMap
            )
            return {
              ...currentExam,
              summary,
            }
          })
        )
      } catch (error) {
        console.error('Sınav soru atamaları alınamadı', error)
      }
    },
    [setBookletQuestionOrders, setOptionMappings, setQuestionAssignments, setExamResults]
  )

  const persistExamResult = useCallback(
    async (exam: OpticalFormExamResult): Promise<void> => {
      const periodId = selectedExamPeriodRef.current
      if (!periodId || !exam || !exam.examId) {
        return
      }

      try {
        await saveOpticalExamResult(periodId, exam)
      } catch (error: any) {
        console.error('Optik sonuç kaydedilirken hata oluştu', error)
        const message =
          error?.response?.data?.message ||
          (Array.isArray(error?.response?.data?.errors)
            ? error.response.data.errors.join('\n')
            : 'Optik sonuç kaydedilirken bir hata oluştu.')
        setProcessErrors(prev => {
          if (prev.includes(message)) {
            return prev
          }
          return [...prev, message]
        })
      }
    },
    [setProcessErrors]
  )

  const ensureExamConfiguration = useCallback(
    (examId: number): ExamConfiguration => {
      const existing = examConfigurationsRef.current[examId]
      if (existing) {
        return existing
      }

      const exam = examResultsRef.current.find(item => item.examId === examId)
      const initial = exam
        ? createInitialConfiguration(exam)
        : {
            questions: {},
            penalty: {
              applyPenalty: false,
              wrongCount: 4,
              correctDeduction: 1,
            },
            note: '',
            masterBookletCode: MASTER_BOOKLET_CODE,
            selectedBookletCode: MASTER_BOOKLET_CODE,
          }

      setExamConfigurations(prev => ({
        ...prev,
        [examId]: initial,
      }))

      return initial
    },
    []
  )

  const updateExamConfiguration = useCallback(
    (examId: number, updater: (config: ExamConfiguration) => ExamConfiguration): void => {
      const baseConfig = cloneExamConfiguration(ensureExamConfiguration(examId))
      const updatedConfig = updater(baseConfig)

      setExamConfigurations(prev => ({
        ...prev,
        [examId]: updatedConfig,
      }))

      setExamResults(prev =>
        prev.map(exam => {
          if (exam.examId !== examId) {
            return exam
          }
          const assignments = questionAssignmentsRef.current[examId] ?? null
          const recalculated = recalculateExamWithConfiguration(exam, updatedConfig, assignments)
          persistExamResult(recalculated)
          return recalculated
        })
      )
    },
    [ensureExamConfiguration, persistExamResult]
  )

  const applyExamUpdate = useCallback(
    (examId: number, updater: (exam: OpticalFormExamResult) => OpticalFormExamResult) => {
      setExamResults(prev =>
        prev.map(exam => {
          if (exam.examId !== examId) {
            return exam
          }

          const updatedExam = updater(exam)
          const config = examConfigurationsRef.current[examId]
          if (config) {
            const assignments = questionAssignmentsRef.current[examId] ?? null
            const recalculated = recalculateExamWithConfiguration(updatedExam, config, assignments)
            persistExamResult(recalculated)
            return recalculated
          }

          persistExamResult(updatedExam)
          return updatedExam
        })
      )
    },
    [persistExamResult]
  )

  useEffect(() => {
    getExamPeriods()
      .then(periods => setExamPeriods(Array.isArray(periods) ? periods : []))
      .catch(err => {
        console.error('Sınav dönemleri alınamadı', err)
        Swal.fire('Hata', 'Sınav dönemleri yüklenirken bir hata oluştu.', 'error')
      })
  }, [])

  useEffect(() => {
    if (!selectedExamPeriod) {
      setExamOptions([])
      setSelectedExamIds([])
      setIsExamOptionsLoading(false)
      return
    }

    setIsExamOptionsLoading(true)
    getExamsByPeriod(selectedExamPeriod)
      .then(exams => setExamOptions(Array.isArray(exams) ? exams : []))
      .catch(err => {
        console.error('Sınav listesi alınamadı', err)
        Swal.fire('Hata', 'Seçilen döneme ait sınavlar yüklenemedi.', 'error')
      })
      .finally(() => {
        setIsExamOptionsLoading(false)
      })
  }, [selectedExamPeriod])

  useEffect(() => {
    if (examResults.length === 0) {
      return
    }

    examResults.forEach(exam => {
      loadExamAssignments(exam)
    })
  }, [examResults, loadExamAssignments])

  const examSelectOptions: SelectOption[] = useMemo(
    () =>
      examOptions.map(option => ({
        value: option.id,
        label:
          option.studentCount != null
            ? `${option.name} (${option.studentCount} öğrenci)`
            : option.name,
      })),
    [examOptions]
  )

  const selectedExamSelectOptions = useMemo(
    () => examSelectOptions.filter(option => selectedExamIds.includes(option.value)),
    [examSelectOptions, selectedExamIds]
  )

  const handleExamSelection = (selected: any, actionMeta?: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0) || actionMeta?.action === 'clear') {
      setSelectedExamIds([])
      return
    }

    const selectedArray = Array.isArray(selected) ? selected : []
    const baseIds = new Set<number>()

    selectedArray.forEach((option: any) => {
      const value = Number(option?.value)
      if (!Number.isFinite(value)) {
        return
      }

      const baseExamId = getBaseExamId(value)
      if (Number.isFinite(baseExamId)) {
        baseIds.add(baseExamId)
      }
    })

    if (actionMeta?.action === 'remove-value' || actionMeta?.action === 'pop-value') {
      const removedValue = Number(actionMeta?.removedValue?.value)
      if (Number.isFinite(removedValue)) {
        const removedBaseId = getBaseExamId(removedValue)
        if (Number.isFinite(removedBaseId)) {
          baseIds.delete(removedBaseId)
        }
      }
    }

    if (baseIds.size === 0) {
      setSelectedExamIds([])
      return
    }

    const finalIds = Array.from(baseIds).flatMap(baseId => getGroupExamIds(baseId))
    const uniqueIds = Array.from(
      new Set(
        finalIds.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      )
    )

    setSelectedExamIds(uniqueIds)
  }

  const selectedExamGroups = useMemo(() => {
    const groups: {
      baseExamId: number
      title: string
      exams: ExamOption[]
      totalStudentCount: number
    }[] = []
    const processedBaseIds = new Set<number>()

    selectedExamIds.forEach(examId => {
      const baseExamId = getBaseExamId(examId)
      if (!Number.isFinite(baseExamId) || processedBaseIds.has(baseExamId)) {
        return
      }

      const groupExams = examGroupsByBaseId[baseExamId] ?? []
      const normalizedExams = groupExams.length > 0
        ? groupExams
        : examOptions.filter(option => option.id === baseExamId)

      if (normalizedExams.length === 0) {
        return
      }

      const sortedExams = [...normalizedExams].sort((a, b) => {
        const aIsBase = a.id === baseExamId
        const bIsBase = b.id === baseExamId
        if (aIsBase && !bIsBase) {
          return -1
        }
        if (!aIsBase && bIsBase) {
          return 1
        }
        const aName = typeof a.name === 'string' ? a.name : ''
        const bName = typeof b.name === 'string' ? b.name : ''
        return aName.localeCompare(bName, 'tr', {sensitivity: 'base'})
      })

      const totalStudentCount = sortedExams.reduce((total, exam) => {
        const count = typeof exam.studentCount === 'number' && Number.isFinite(exam.studentCount)
          ? exam.studentCount
          : 0
        return total + count
      }, 0)

      const baseExam = sortedExams.find(exam => exam.id === baseExamId) ?? sortedExams[0]
      const title = baseExam?.name ?? sortedExams[0]?.name ?? 'Sınav'

      groups.push({
        baseExamId,
        title,
        exams: sortedExams,
        totalStudentCount,
      })
      processedBaseIds.add(baseExamId)
    })

    return groups
  }, [selectedExamIds, getBaseExamId, examGroupsByBaseId, examOptions])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length === 0) {
      return
    }
    setUploadedFiles(prev => [...prev, ...files])
    event.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, idx) => idx !== index))
  }

  const handleProcess = async () => {
    if (!selectedExamPeriod) {
      Swal.fire('Uyarı', 'Lütfen bir sınav dönemi seçin.', 'warning')
      return
    }
    if (selectedExamIds.length === 0) {
      Swal.fire('Uyarı', 'Lütfen en az bir sınav seçin.', 'warning')
      return
    }
    if (uploadedFiles.length === 0) {
      Swal.fire('Uyarı', 'Lütfen en az bir DAT dosyası yükleyin.', 'warning')
      return
    }

    const hasPreviouslyProcessedExam = selectedExamIds.some(examId => {
      const baseExamId = getBaseExamId(examId)
      if (!Number.isFinite(baseExamId)) {
        const exam = examOptions.find(option => option.id === examId)
        return Boolean(exam?.hasOpticalReading)
      }

      const group = examGroupsByBaseId[baseExamId]
      if (Array.isArray(group) && group.length > 0) {
        return group.some(option => Boolean(option?.hasOpticalReading))
      }

      const fallbackExam = examOptions.find(option => option.id === baseExamId)
      return Boolean(fallbackExam?.hasOpticalReading)
    })

    if (hasPreviouslyProcessedExam) {
      const confirmation = await Swal.fire({
        title: 'Dikkat',
        text: 'Seçilen sınav daha önce okunmuş. Tekrar okumak istediğinizden emin misiniz?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Evet, devam et',
        cancelButtonText: 'Vazgeç',
      })

      if (!confirmation.isConfirmed) {
        return
      }
    }

    const formData = new FormData()
    formData.append('ExamPeriodId', String(selectedExamPeriod))
    selectedExamIds.forEach(id => formData.append('ExamIds', String(id)))
    uploadedFiles.forEach(file => formData.append('Files', file, file.name))

    setIsProcessing(true)
    setProcessErrors([])
    setQuestionReviewCache({})
    setQuestionAssignments({})
    setOptionMappings({})
    questionAssignmentsRef.current = {}
    optionMappingsRef.current = {}
    setQuestionPreviewContext(null)
    try {
      const response = await processOpticalForms(formData)
      const normalized = Array.isArray(response.exams)
        ? response.exams.map(normalizeExamResult)
        : []
      const alignedExams = alignExamBookletsWithBaseExam(normalized)
      const configurationMap: Record<number, ExamConfiguration> = {}
      const recalculated = alignedExams.map(exam => {
        const config = createInitialConfiguration(exam)
        configurationMap[exam.examId] = config
        return recalculateExamWithConfiguration(exam, config, null)
      })

      setExamConfigurations(configurationMap)
      setExamResults(recalculated)
      setProcessErrors(response.errors ?? [])
      await Promise.all(recalculated.map(exam => persistExamResult(exam)))
      await Promise.all(recalculated.map(exam => loadExamAssignments(exam)))
    } catch (error: any) {
      console.error('Optik okuma başlatılırken hata oluştu', error)
      const message =
        error?.response?.data?.message ||
        (Array.isArray(error?.response?.data?.errors)
          ? error.response.data.errors.join('\n')
          : 'Optik okuma sırasında bir hata oluştu.')
      Swal.fire('Hata', message, 'error')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleAssignStudent = useCallback(
    (examId: number, entryId: string, studentSemesterCourseId: number | null) => {
      applyExamUpdate(examId, exam => {
        const student = studentSemesterCourseId
          ? exam.students.find(student => student.studentSemesterCourseId === studentSemesterCourseId)
          : undefined

        return applyEntriesUpdate(exam, entries => {
          const updated = entries.map(entry => {
            if (entry.entryId !== entryId) {
              if (
                studentSemesterCourseId != null &&
                entry.studentSemesterCourseId === studentSemesterCourseId &&
                entry.entryId !== entryId
              ) {
                return {
                  ...entry,
                  status: OpticalStudentEntryStatus.Duplicate,
                  isPrimaryMatch: false,
                  previousStatus: entry.previousStatus ?? null,
                  previousIsPrimaryMatch: entry.previousIsPrimaryMatch ?? null,
                }
              }
              return entry
            }

            if (!studentSemesterCourseId || !student) {
              return {
                ...entry,
                studentSemesterCourseId: undefined,
                studentName: undefined,
                studentSurname: undefined,
                status: OpticalStudentEntryStatus.Unregistered,
                isPrimaryMatch: false,
                previousStatus: null,
                previousIsPrimaryMatch: null,
              }
            }

            return {
              ...entry,
              studentSemesterCourseId: student.studentSemesterCourseId,
              studentName: student.name ?? '',
              studentSurname: student.surname ?? '',
              status: OpticalStudentEntryStatus.Matched,
              isPrimaryMatch: true,
              notes: null,
              previousStatus: null,
              previousIsPrimaryMatch: null,
            }
          })

          if (studentSemesterCourseId != null && student) {
            return updated.map(entry => {
              if (entry.studentSemesterCourseId !== studentSemesterCourseId) {
                return entry
              }

              if (entry.entryId === entryId) {
                return {
                  ...entry,
                  status: OpticalStudentEntryStatus.Matched,
                  isPrimaryMatch: true,
                  notes: null,
                  previousStatus: null,
                  previousIsPrimaryMatch: null,
                }
              }

              return {
                ...entry,
                status: OpticalStudentEntryStatus.Duplicate,
                isPrimaryMatch: false,
                previousStatus: entry.previousStatus ?? null,
                previousIsPrimaryMatch: entry.previousIsPrimaryMatch ?? null,
              }
            })
          }

          return updated
        })
      })
    },
    [applyExamUpdate]
  )

  const handleBookletUpdate = useCallback(
    (examId: number, entryId: string, value: string) => {
      const currentExam = examResultsRef.current.find(item => item.examId === examId)
      const currentEntry = currentExam?.entries.find(item => item.entryId === entryId)
      const normalized = normalizeBookletCode(value)
      const sanitized = normalized ?? ''

      applyExamUpdate(examId, exam =>
        applyEntriesUpdate(exam, entries =>
          entries.map(entry => {
            if (entry.entryId !== entryId) {
              return entry
            }

            const remappedEntry = remapEntryToBooklet(entry, currentExam, sanitized)
            const updatedStatus =
              entry.status === OpticalStudentEntryStatus.BookletMismatch && sanitized
                ? OpticalStudentEntryStatus.Unregistered
                : entry.status

            return {
              ...remappedEntry,
              bookletCode: sanitized,
              notes: sanitized ? null : entry.notes,
              status: updatedStatus,
            }
          })
        )
      )

      if (currentEntry?.studentSemesterCourseId) {
        handleAssignStudent(examId, entryId, currentEntry.studentSemesterCourseId)
      }
    },
    [applyExamUpdate, handleAssignStudent]
  )

  const handleStudentNumberChange = useCallback(
    (examId: number, entryId: string, value: string) => {
      const currentExam = examResultsRef.current.find(item => item.examId === examId)
      const trimmed = value?.trim() ?? ''
      const normalizedInput = trimmed.replace(/\s+/g, '')
      const matchedStudent = currentExam?.students.find(student =>
        student.studentNumber.replace(/\s+/g, '') === normalizedInput
      )

      applyExamUpdate(examId, exam =>
        applyEntriesUpdate(exam, entries =>
          entries.map(entry => {
            if (entry.entryId !== entryId) {
              return entry
            }

            return {
              ...entry,
              studentNumber: trimmed,
            }
          })
        )
      )

      if (matchedStudent) {
        handleAssignStudent(examId, entryId, matchedStudent.studentSemesterCourseId)
      } else {
        handleAssignStudent(examId, entryId, null)
      }
    },
    [applyExamUpdate, handleAssignStudent]
  )

  const handleMarkPrimary = useCallback((examId: number, entryId: string) => {
    applyExamUpdate(examId, exam => {
      const target = exam.entries.find(entry => entry.entryId === entryId)
      if (!target || !target.studentSemesterCourseId) {
        return exam
      }
      const studentId = target.studentSemesterCourseId
      return applyEntriesUpdate(exam, entries =>
        entries.map(entry => {
          if (entry.studentSemesterCourseId !== studentId) {
            return entry
          }
          if (entry.entryId === entryId) {
            return {
              ...entry,
              status: OpticalStudentEntryStatus.Matched,
              isPrimaryMatch: true,
              previousStatus: null,
              previousIsPrimaryMatch: null,
              notes: null,
            }
          }
          return {
            ...entry,
            status: OpticalStudentEntryStatus.Duplicate,
            isPrimaryMatch: false,
            previousStatus: entry.previousStatus ?? null,
            previousIsPrimaryMatch: entry.previousIsPrimaryMatch ?? null,
          }
        })
      )
    })
  }, [applyExamUpdate])

  const handleToggleSkip = useCallback(
    (examId: number, entryId: string, shouldSkip: boolean) => {
      applyExamUpdate(examId, exam =>
        applyEntriesUpdate(exam, entries =>
          entries.map(entry => {
            if (entry.entryId !== entryId) {
              return entry
            }

            if (shouldSkip) {
              if (entry.status === OpticalStudentEntryStatus.Skipped) {
                return entry
              }

              return {
                ...entry,
                status: OpticalStudentEntryStatus.Skipped,
                isPrimaryMatch: false,
                previousStatus: entry.status,
                previousIsPrimaryMatch: entry.isPrimaryMatch,
              }
            }

            const restoredStatus =
              entry.previousStatus ?? OpticalStudentEntryStatus.Unregistered
            const restoredIsPrimary = Boolean(entry.previousIsPrimaryMatch)

            return {
              ...entry,
              status: restoredStatus,
              isPrimaryMatch:
                restoredStatus === OpticalStudentEntryStatus.Matched
                  ? restoredIsPrimary
                  : false,
              previousStatus: null,
              previousIsPrimaryMatch: null,
            }
          })
        )
      )
    },
    [applyExamUpdate]
  )

  const handleAcceptedOptionsChange = useCallback(
    (examId: number, examQuestionId: number, value: string) => {
      const parsedOptions = parseAcceptedOptionsInput(value)
      const normalizedInput = normalizeAcceptedOptionsList(parsedOptions)
      const canonicalValue = normalizedInput.join(', ')
      const formattedValue = formatAcceptedOptionsInput(value, normalizedInput)

      updateAcceptedOptionsInputCache(examId, examQuestionId, formattedValue, canonicalValue)

      updateExamConfiguration(examId, config => {
        const existing = config.questions[examQuestionId]

        const baseQuestion: ExamQuestionConfig = existing
          ? {
              ...existing,
              acceptedOptionsByBooklet: cloneAcceptedOptionsMap(existing.acceptedOptionsByBooklet),
              defaultAcceptedOptions: [...existing.defaultAcceptedOptions],
            }
          : {
              examQuestionId,
              acceptedOptionsByBooklet: {},
              defaultAcceptedOptions: [],
              isCancelled: false,
              isMultipleChoice: false,
              point: 0,
            }

        const normalizedMaster = normalizeBookletCode(config.masterBookletCode)

        const normalizedMap: QuestionAcceptedOptionsMap = {}
        Object.entries(baseQuestion.acceptedOptionsByBooklet).forEach(([code, options]) => {
          const normalizedCode = normalizeBookletCode(code)
          if (normalizedCode) {
            normalizedMap[normalizedCode] = normalizeAcceptedOptionsList(options)
          }
        })
        baseQuestion.acceptedOptionsByBooklet = normalizedMap

        baseQuestion.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE] = [...normalizedInput]

        if (normalizedMaster && normalizedMaster !== MASTER_BOOKLET_CODE) {
          baseQuestion.acceptedOptionsByBooklet[normalizedMaster] = [...normalizedInput]
        }

        const questionMappings = optionMappingsRef.current[examId]?.[examQuestionId]
        const masterMapping =
          questionMappings?.[MASTER_BOOKLET_CODE] ??
          (normalizedMaster ? questionMappings?.[normalizedMaster] : undefined) ??
          questionMappings?.[BASE_OPTION_MAPPING_KEY]
        const optionIds = masterMapping ? mapLettersToOptionIds(normalizedInput, masterMapping) : []

        const allCodes = new Set<string>()
        Object.keys(baseQuestion.acceptedOptionsByBooklet).forEach(code => allCodes.add(code))
        if (questionMappings) {
          Object.keys(questionMappings).forEach(code => {
            if (code && code !== BASE_OPTION_MAPPING_KEY) {
              const normalizedCode = normalizeBookletCode(code)
              if (normalizedCode) {
                allCodes.add(normalizedCode)
              }
            }
          })
        }

        if (normalizedMaster) {
          allCodes.add(normalizedMaster)
        }

        allCodes.forEach(code => {
          if (!code) {
            return
          }

          if (code === MASTER_BOOKLET_CODE) {
            baseQuestion.acceptedOptionsByBooklet[code] = [...normalizedInput]
            return
          }

          let translated = [...normalizedInput]
          if (optionIds.length > 0) {
            const mapping = questionMappings?.[code]
            const letters = mapOptionIdsToLetters(optionIds, mapping)
            if (letters.length > 0) {
              translated = letters
            }
          }

          baseQuestion.acceptedOptionsByBooklet[code] = normalizeAcceptedOptionsList(translated)
        })

        baseQuestion.defaultAcceptedOptions = [...normalizedInput]

        return {
          ...config,
          questions: {
            ...config.questions,
            [examQuestionId]: baseQuestion,
          },
        }
      })
    },
    [updateExamConfiguration, updateAcceptedOptionsInputCache]
  )

  const openQuestionPreview = useCallback(
    (examId: number, examQuestionId: number, source: QuestionPreviewSource, bookletCode?: string | null) => {
      setQuestionPreviewContext({
        examId,
        examQuestionId,
        source,
        bookletCode: bookletCode ? normalizeBookletCode(bookletCode) : undefined,
      })
    },
    []
  )

  const showQuestionReview = useCallback(
    async (
      examId: number,
      analysis: OpticalFormQuestionAnalysis,
      difficultyInsight: MetricInsight,
      discriminationInsight: MetricInsight
    ) => {
      if (!isAiActive) {
        return
      }

      const {cacheKey, signature, payload} = buildQuestionReviewData({
        examId,
        analysis,
        difficultyInsight,
        discriminationInsight,
        questionAssignments: questionAssignmentsRef.current,
        optionMappings: optionMappingsRef.current,
        examConfigurations: examConfigurationsRef.current,
      })
      const cached = questionReviewCache[cacheKey]

      if (cached?.signature === signature && cached.comment) {
        if (!cached.formattedHtml) {
          const formatted = formatAiCommentHtml(cached.comment)
          setQuestionReviewCache(prev => ({
            ...prev,
            [cacheKey]: {...cached, formattedHtml: formatted},
          }))
        }
        return
      }

      Swal.fire({
        title: 'Yapay zekâ analizi hazırlanıyor',
        text: 'Soru inceleme asistanı yanıtı bekleniyor...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      try {
        const response = await getOpticalQuestionReview(payload)

        Swal.close()

        const comment = (response?.comment ?? '').trim()
        const normalizedComment =
          comment.length > 0 ? comment : 'Yapay zekâdan yorum alınamadı.'
        const formattedHtml = formatAiCommentHtml(normalizedComment)

        setQuestionReviewCache(prev => ({
          ...prev,
          [cacheKey]: {comment: normalizedComment, formattedHtml, signature},
        }))

        Swal.fire({
          title: 'Tamamlandı',
          text: 'Yapay zekâ yorumu güncellendi.',
          icon: 'success',
          timer: 1600,
          showConfirmButton: false,
        })
      } catch (error: any) {
        Swal.close()
        const message =
          error?.response?.data?.error ??
          error?.response?.data ??
          error?.message ??
          'Yapay zekâ yanıtı alınamadı.'
        const normalizedMessage =
          typeof message === 'string' && message.length > 0
            ? message
            : 'Yapay zekâ yanıtı alınamadı.'
        setQuestionReviewCache(prev => ({
          ...prev,
          [cacheKey]: {error: normalizedMessage, signature},
        }))
        Swal.fire({
          title: 'Hata',
          text: normalizedMessage,
          icon: 'error',
          width: '40rem',
          confirmButtonText: 'Kapat',
        })
      }
    },
    [questionReviewCache]
  )

  const closeQuestionPreview = useCallback(() => {
    setQuestionPreviewContext(null)
  }, [])

  const handleToggleCancellation = useCallback(
    (examId: number, examQuestionId: number, isCancelled: boolean) => {
      updateExamConfiguration(examId, config => {
        const existing = config.questions[examQuestionId]
        if (!existing) {
          return config
        }
        return {
          ...config,
          questions: {
            ...config.questions,
            [examQuestionId]: {
              ...existing,
              isCancelled,
            },
          },
        }
      })
    },
    [updateExamConfiguration]
  )

  const handlePenaltyToggle = useCallback(
    (examId: number, applyPenalty: boolean) => {
      updateExamConfiguration(examId, config => ({
        ...config,
        penalty: {
          ...config.penalty,
          applyPenalty,
        },
      }))
    },
    [updateExamConfiguration]
  )

  const handlePenaltyValueChange = useCallback(
    (examId: number, key: 'wrongCount' | 'correctDeduction', value: string) => {
      const numeric = Number(value)
      const sanitized = Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : 0
      updateExamConfiguration(examId, config => ({
        ...config,
        penalty: {
          ...config.penalty,
          [key]: sanitized,
        },
      }))
    },
    [updateExamConfiguration]
  )

  const handleNoteChange = useCallback(
    (examId: number, value: string) => {
      updateExamConfiguration(examId, config => ({
        ...config,
        note: value,
      }))
    },
    [updateExamConfiguration]
  )

  const handleToggleBookletSection = useCallback((examId: number, code: string | null) => {
    setExpandedBooklets(prev => {
      const normalized = normalizeBookletCode(code)
      const current = prev[examId] ?? null
      return {
        ...prev,
        [examId]: current === normalized ? null : normalized,
      }
    })
  }, [])

  const handleOpenEntryDetails = useCallback((examId: number, entryId: string) => {
    setSelectedEntryDetail({examId, entryId})
  }, [])

  const closeDetailModal = useCallback(() => {
    setSelectedEntryDetail(null)
  }, [])

  const detailModalContext = useMemo(() => {
    if (!selectedEntryDetail) {
      return null
    }

    const exam = examResults.find(item => item.examId === selectedEntryDetail.examId)
    if (!exam) {
      return null
    }

    const entry = exam.entries.find(item => item.entryId === selectedEntryDetail.entryId)
    if (!entry) {
      return null
    }

    return {exam, entry}
  }, [examResults, selectedEntryDetail])

  const blockingStatusSet = useMemo(
    () =>
      new Set<OpticalStudentEntryStatus>([
        OpticalStudentEntryStatus.Unregistered,
        OpticalStudentEntryStatus.Ambiguous,
        OpticalStudentEntryStatus.BookletMismatch,
        OpticalStudentEntryStatus.InvalidLine,
      ]),
    []
  )

  const hasBlockingIssues = useMemo(
    () =>
      examResults.some(exam =>
        exam.entries.some(entry => blockingStatusSet.has(entry.status))
      ),
    [examResults, blockingStatusSet]
  )

  const hasAnyMatched = useMemo(
    () =>
      examResults.some(exam =>
        exam.entries.some(
          entry =>
            entry.status === OpticalStudentEntryStatus.Matched &&
            entry.isPrimaryMatch &&
            entry.studentSemesterCourseId != null
        )
      ),
    [examResults]
  )

  const canFinalize =
    !isProcessing &&
    !isFinalizing &&
    selectedExamPeriod != null &&
    examResults.length > 0 &&
    hasAnyMatched &&
    !hasBlockingIssues

  const buildFinalizePayload = (): OpticalFormFinalizePayload | null => {
    if (selectedExamPeriod == null) {
      return null
    }

    const exams = examResults.reduce<OpticalFormFinalizeExamPayload[]>((acc, exam) => {
      const config = examConfigurationsRef.current[exam.examId] ?? createInitialConfiguration(exam)

      const entries: OpticalFormFinalizeEntryPayload[] = exam.entries
        .filter(
          entry =>
            entry.status === OpticalStudentEntryStatus.Matched &&
            entry.isPrimaryMatch &&
            entry.studentSemesterCourseId != null
        )
        .map(entry => ({
          studentSemesterCourseId: entry.studentSemesterCourseId!,
          bookletCode: entry.bookletCode,
          answers: entry.answers,
        }))

      if (entries.length === 0) {
        return acc
      }

      const questionAdjustments: OpticalFormFinalizeQuestionAdjustment[] = Object.values(config.questions).map(question => {
        const normalizedMap: Record<string, string[]> = {}
        Object.entries(question.acceptedOptionsByBooklet).forEach(([code, options]) => {
          const normalizedCode = normalizeBookletCode(code)
          if (!normalizedCode) {
            return
          }

          normalizedMap[normalizedCode] = normalizeAcceptedOptionsList(options)
        })

        const normalizedMaster = normalizeBookletCode(config.masterBookletCode)
        const masterOptionsSource = normalizedMaster && normalizedMap[normalizedMaster]?.length
          ? normalizedMap[normalizedMaster]
          : question.defaultAcceptedOptions
        const acceptedOptions = normalizeAcceptedOptionsList(masterOptionsSource)

        if (normalizedMaster && !normalizedMap[normalizedMaster]) {
          normalizedMap[normalizedMaster] = [...acceptedOptions]
        }

        const mapping = optionMappingsRef.current[exam.examId]?.[question.examQuestionId]
        let acceptedQuestionItemIds: number[] | undefined
        if (mapping) {
          const masterMapping =
            (normalizedMaster && mapping[normalizedMaster]) || mapping[BASE_OPTION_MAPPING_KEY]
          if (masterMapping) {
            const mappedIds = mapLettersToOptionIds(acceptedOptions, masterMapping)
            if (mappedIds.length > 0) {
              acceptedQuestionItemIds = mappedIds
            } else if (question.defaultAcceptedOptions.length > 0) {
              const fallbackIds = mapLettersToOptionIds(
                normalizeAcceptedOptionsList(question.defaultAcceptedOptions),
                masterMapping
              )
              if (fallbackIds.length > 0) {
                acceptedQuestionItemIds = fallbackIds
              }
            }
          }
        }

        if (acceptedOptions.length === 0 && acceptedQuestionItemIds === undefined) {
          acceptedQuestionItemIds = []
        }

        return {
          examQuestionId: question.examQuestionId,
          acceptedOptions,
          isCancelled: question.isCancelled,
          bookletAcceptedOptions: normalizedMap,
          acceptedQuestionItemIds,
        }
      })

      const options: OpticalFormFinalizeExamOptions = {
        applyWrongPenalty: Boolean(config.penalty.applyPenalty),
        wrongCountForPenalty: Number(config.penalty.wrongCount ?? 0),
        correctCountToSubtract: Number(config.penalty.correctDeduction ?? 0),
      }

      const objectionNote = config.note?.trim() ? config.note.trim() : null

      acc.push({
        examId: exam.examId,
        entries,
        questionAdjustments,
        options,
        objectionNote,
      })

      return acc
    }, [])

    if (exams.length === 0) {
      return null
    }

    return {
      examPeriodId: selectedExamPeriod,
      exams,
    }
  }

  const handleFinalize = async () => {
    const payload = buildFinalizePayload()
    if (!payload) {
      Swal.fire('Uyarı', 'Kaydedilecek sonuç bulunamadı.', 'warning')
      return
    }

    setIsFinalizing(true)
    try {
      const response = await finalizeOpticalForms(payload)
      setProcessErrors(response.errors ?? [])
      Swal.fire('Başarılı', response.message ?? 'Sonuçlar kaydedildi.', 'success')
    } catch (error: any) {
      console.error('Optik sonuçları kaydedilirken hata oluştu', error)
      const message =
        error?.response?.data?.message ||
        (Array.isArray(error?.response?.data?.errors)
          ? error.response.data.errors.join('\n')
          : 'Optik sonuçları kaydedilirken bir hata oluştu.')
      Swal.fire('Hata', message, 'error')
    } finally {
      setIsFinalizing(false)
    }
  }

  const renderEntryActions = (exam: OpticalFormExamResult, entry: OpticalFormStudentEntry) => {
    const studentOptions = exam.students.map(student => ({
      value: student.studentSemesterCourseId,
      label: `${student.studentNumber} - ${[student.name, student.surname]
        .filter(Boolean)
        .join(' ')}`.trim(),
    }))

    const currentValue = entry.studentSemesterCourseId
      ? studentOptions.find(option => option.value === entry.studentSemesterCourseId) ?? null
      : null

    const isSkipped = entry.status === OpticalStudentEntryStatus.Skipped
    const canSkip = blockingStatusSet.has(entry.status) || isSkipped

    return (
      <div className='d-flex flex-column gap-2'>
        <CustomSelect
          classNamePrefix='react-select'
          options={studentOptions}
          value={currentValue}
          onChange={(option: any) =>
            handleAssignStudent(
              exam.examId,
              entry.entryId,
              option ? Number(option.value) : null
            )
          }
          isClearable={true}
          placeholder='Öğrenci Ata'
        />
        {entry.status === OpticalStudentEntryStatus.Duplicate && entry.studentSemesterCourseId && (
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={() => handleMarkPrimary(exam.examId, entry.entryId)}
          >
            Birincil Yap
          </button>
        )}
        {canSkip && (
          <button
            type='button'
            className={`btn btn-sm ${isSkipped ? 'btn-light-success' : 'btn-light-warning'}`}
            onClick={() => handleToggleSkip(exam.examId, entry.entryId, !isSkipped)}
          >
            {isSkipped ? 'Yoksaymayı Geri Al' : 'Yoksay'}
          </button>
        )}
        {isSkipped && (
          <small className='text-muted'>Bu kayıt sonuçlara dahil edilmeyecek.</small>
        )}
      </div>
    )
  }

  return (
    <div className='container-fluid py-5'>
      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Optik Sonuç Okuma</h3>
        </div>
        <div className='card-body row g-5'>
          <div className='col-lg-3'>
            <label className='form-label'>Sınav Dönemi</label>
            <select
              className='form-select'
              value={selectedExamPeriod ?? ''}
              onChange={event => {
                const value = event.target.value
                setSelectedExamPeriod(value ? Number(value) : null)
                setExamResults([])
                setExamConfigurations({})
                setProcessErrors([])
                setQuestionReviewCache({})
              }}
            >
              <option value=''>Seçiniz</option>
              {examPeriods.map(period => (
                <option key={period.id} value={period.id}>
                  {period.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-lg-4'>
            <label className='form-label'>Sınavlar</label>
            <CustomSelect
              classNamePrefix='react-select'
              options={examSelectOptions}
              value={selectedExamSelectOptions}
              isMulti={true}
              isLoading={isExamOptionsLoading}
              isDisabled={isExamOptionsLoading}
              onChange={handleExamSelection}
              placeholder='Sınav seçin'
            />
            {selectedExamGroups.length > 0 && (
              <div className='mt-3'>
                <div className='fw-semibold text-gray-700 mb-2'>Seçilen Sınavlar</div>
                <div className='d-flex flex-column gap-3'>
                  {selectedExamGroups.map(group => (
                    <div
                      key={`selected-exam-group-${group.baseExamId}`}
                      className='border rounded p-3 bg-light d-flex flex-column gap-2'
                    >
                      <div className='d-flex flex-wrap justify-content-between align-items-center gap-2'>
                        <div className='fw-semibold text-gray-900'>{group.title}</div>
                        <div className='d-flex align-items-center gap-2'>
                          <span className='badge badge-light-primary'>
                            {group.totalStudentCount} öğrenci
                          </span>
                          {group.exams.length > 1 && (
                            <span className='badge badge-light-info'>Birleştirilmiş {group.exams.length} sınav</span>
                          )}
                        </div>
                      </div>
                      <div className='d-flex flex-column gap-1'>
                        {group.exams.map(exam => (
                          <div
                            key={`selected-exam-${exam.id}`}
                            className='d-flex justify-content-between align-items-center text-gray-800'
                          >
                            <span>{exam.name}</span>
                            {typeof exam.studentCount === 'number' && Number.isFinite(exam.studentCount) && (
                              <span className='text-muted small'>{exam.studentCount} öğrenci</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className='col-lg-5'>
            <label className='form-label'>DAT Dosyaları</label>
            <input
              type='file'
              className='form-control'
              multiple
              accept='.dat,.txt'
              onChange={handleFileChange}
            />
            {uploadedFiles.length > 0 && (
              <div className='mt-3'>
                <div className='fw-semibold mb-2'>Yüklenecek Dosyalar</div>
                <ul className='list-group list-group-flush'>
                  {uploadedFiles.map((file, index) => (
                    <li className='list-group-item d-flex justify-content-between align-items-center' key={`${file.name}-${index}`}>
                      <span>{file.name}</span>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger'
                        onClick={() => handleRemoveFile(index)}
                      >
                        Kaldır
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className='card-footer d-flex justify-content-between align-items-center'>
          <div>
            <button
              type='button'
              className='btn btn-primary me-3'
              disabled={isProcessing}
              onClick={handleProcess}
            >
              {isProcessing ? 'Okunuyor...' : 'Okumayı Başlat'}
            </button>
            <button
              type='button'
              className='btn btn-success'
              disabled={!canFinalize}
              onClick={handleFinalize}
            >
              {isFinalizing ? 'Kaydediliyor...' : 'Okumayı Tamamla'}
            </button>
          </div>
          {hasBlockingIssues && (
            <span className='text-danger fw-semibold'>Önce hatalı kayıtları düzeltmeli veya yoksaymalısınız.</span>
          )}
        </div>
      </div>

      {processErrors.length > 0 && (
        <div className='alert alert-warning'>
          <div className='fw-bold mb-2'>Uyarılar</div>
          <ul className='mb-0'>
            {processErrors.map((error, index) => (
              <li key={`${error}-${index}`}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {examResults.map(exam => {
        const statusCounts = exam.entries.reduce(
          (acc, entry) => {
            acc[entry.status] = (acc[entry.status] || 0) + 1
            return acc
          },
          {} as Record<OpticalStudentEntryStatus, number>
        )

        const config = examConfigurations[exam.examId] ?? createInitialConfiguration(exam)
        const summary = exam.summary

        const questionMappingForExam = optionMappings[exam.examId] ?? {}
        const assignmentsForExam = questionAssignments[exam.examId] ?? {}
        const bookletOrderMapForExam = bookletQuestionOrders[exam.examId] ?? {}
        const normalizedMasterCode = normalizeBookletCode(exam.masterBookletCode)
        const masterOrderMap =
          (normalizedMasterCode && bookletOrderMapForExam[normalizedMasterCode]) ??
          bookletOrderMapForExam[MASTER_BOOKLET_CODE] ??
          null
        const questionLookup = exam.questions.reduce<Record<number, OpticalFormQuestion>>((acc, question) => {
          acc[question.examQuestionId] = question
          return acc
        }, {})

        const resolveQuestionNumber = (
          examQuestionId: number,
          fallback?: number | null
        ): number | null => {
          const masterOrder = masterOrderMap?.[examQuestionId]
          if (typeof masterOrder === 'number' && Number.isFinite(masterOrder)) {
            return masterOrder
          }

          const assignmentOrder = assignmentsForExam[examQuestionId]?.order
          if (typeof assignmentOrder === 'number' && Number.isFinite(assignmentOrder)) {
            return assignmentOrder
          }

          if (typeof fallback === 'number' && Number.isFinite(fallback)) {
            return fallback
          }

          return null
        }

        const availableBookletOptions = Array.isArray(exam.booklets)
          ? exam.booklets
              .map(booklet => {
                const normalizedCode = normalizeBookletCode(booklet.code)
                if (!normalizedCode) {
                  return null
                }

                return {
                  value: normalizedCode,
                  label: booklet.code?.trim() || normalizedCode,
                  questionCount: Array.isArray(booklet.questions) ? booklet.questions.length : 0,
                }
              })
              .filter((option): option is {value: string; label: string; questionCount: number} => Boolean(option))
          : []

        const bookletQuestionLookup = Array.isArray(exam.booklets)
          ? exam.booklets.reduce<
              Record<string, {label: string; questions: Record<number, OpticalFormBookletQuestion>}>
            >(
              (acc, booklet) => {
                const normalizedCode = normalizeBookletCode(booklet.code)
                if (!normalizedCode) {
                  return acc
                }

                const questionMap = Array.isArray(booklet.questions)
                  ? booklet.questions.reduce<Record<number, OpticalFormBookletQuestion>>((questionAcc, bookletQuestion) => {
                      questionAcc[bookletQuestion.examQuestionId] = bookletQuestion
                      return questionAcc
                    }, {})
                  : {}

                acc[normalizedCode] = {
                  label: booklet.code?.trim() || normalizedCode,
                  questions: questionMap,
                }
                return acc
              },
              {}
            )
          : {}

        const expandedBookletState = expandedBooklets[exam.examId]
        const expandedBookletCode = expandedBookletState === undefined ? null : expandedBookletState

        const bookletCounts = exam.entries.reduce<Record<string, {label: string; count: number}>>((acc, entry) => {
          const normalized = normalizeBookletCode(entry.bookletCode) ?? 'UNSPECIFIED'
          const label = entry.bookletCode?.trim() || (normalized === 'UNSPECIFIED' ? 'Belirtilmemiş' : normalized)
          if (!acc[normalized]) {
            acc[normalized] = {label, count: 0}
          }
          acc[normalized].count += 1
          return acc
        }, {})

        const bookletCountList = Object.values(bookletCounts)

        const cronbachValue = summary?.cronbachAlpha01 ?? summary?.cronbachAlpha ?? null
        const cronbachComment = summary ? getCronbachAlphaComment(cronbachValue ?? null) : null
        const learningOutcomeReliabilities = summary?.learningOutcomeReliabilities ?? []
        const hasLearningOutcomeReliability = learningOutcomeReliabilities.length > 0

        const effectiveOrderMap: Record<string, BookletQuestionOrderMap> = {}

        Object.entries(bookletOrderMapForExam).forEach(([codeKey, mapping]) => {
          const normalizedKey = normalizeBookletCode(codeKey)
          if (!normalizedKey || !mapping) {
            return
          }

          const effectiveKey =
            normalizedKey === MASTER_BOOKLET_CODE && normalizedMasterCode
              ? normalizedMasterCode
              : normalizedKey

          if (!effectiveOrderMap[effectiveKey]) {
            effectiveOrderMap[effectiveKey] = {}
          }

          Object.entries(mapping).forEach(([questionIdKey, orderValue]) => {
            const questionId = typeof questionIdKey === 'number' ? questionIdKey : Number(questionIdKey)
            const numericOrder =
              typeof orderValue === 'number' && Number.isFinite(orderValue)
                ? orderValue
                : Number(orderValue)

            if (Number.isFinite(questionId) && Number.isFinite(numericOrder)) {
              effectiveOrderMap[effectiveKey][questionId] = Number(numericOrder)
            }
          })
        })

        const bookletLabels: Record<string, string> = {}
        Object.entries(bookletQuestionLookup).forEach(([code, data]) => {
          bookletLabels[code] = data.label
        })

        if (normalizedMasterCode && !bookletLabels[normalizedMasterCode]) {
          bookletLabels[normalizedMasterCode] = normalizedMasterCode
        }

        if (!normalizedMasterCode && effectiveOrderMap[MASTER_BOOKLET_CODE]) {
          bookletLabels[MASTER_BOOKLET_CODE] = 'Master Kitapçık'
        }

        const codesToDisplay = new Set<string>()

        Object.keys(effectiveOrderMap).forEach(code => {
          if (code === MASTER_BOOKLET_CODE && normalizedMasterCode) {
            codesToDisplay.add(normalizedMasterCode)
          } else {
            codesToDisplay.add(code)
          }
        })

        Object.keys(bookletQuestionLookup).forEach(code => {
          codesToDisplay.add(code)
        })

        if (normalizedMasterCode) {
          codesToDisplay.add(normalizedMasterCode)
        }

        const questionBookletOrders = exam.questions.reduce<
          Record<number, {label: string; questionNumber: number | null}[]>
        >((acc, question) => {
          const examQuestionId = question.examQuestionId

          codesToDisplay.forEach(code => {
            const label = bookletLabels[code] ?? code
            const orderMapForCode =
              effectiveOrderMap[code] ??
              (code === normalizedMasterCode ? effectiveOrderMap[MASTER_BOOKLET_CODE] : undefined)

            const orderNumber = orderMapForCode?.[examQuestionId]

            const fallbackQuestion = bookletQuestionLookup[code]?.questions?.[examQuestionId]
            const fallbackNumber = (() => {
              const opticalOrder = fallbackQuestion?.opticalOrder
              if (typeof opticalOrder === 'number' && Number.isFinite(opticalOrder) && opticalOrder > 0)
              {
                return opticalOrder
              }
              const questionNumber = fallbackQuestion?.questionNumber
              return typeof questionNumber === 'number' && Number.isFinite(questionNumber)
                ? questionNumber
                : null
            })()
            const resolvedNumber = (() => {
              if (typeof orderNumber === 'number' && Number.isFinite(orderNumber)) {
                return orderNumber
              }
              if (typeof fallbackNumber === 'number' && Number.isFinite(fallbackNumber)) {
                return fallbackNumber
              }
              if (code === normalizedMasterCode) {
                return resolveQuestionNumber(examQuestionId, question.questionNumber ?? null)
              }
              return null
            })()

            if (!acc[examQuestionId]) {
              acc[examQuestionId] = []
            }

            const existing = acc[examQuestionId].find(item => item.label === label)
            if (existing) {
              if (existing.questionNumber == null && resolvedNumber != null) {
                existing.questionNumber = resolvedNumber
              }
            } else {
              acc[examQuestionId].push({
                label,
                questionNumber: resolvedNumber != null ? resolvedNumber : null,
              })
            }
          })

          return acc
        }, {})

        const masterLabel = normalizedMasterCode
          ? bookletLabels[normalizedMasterCode] ?? normalizedMasterCode
          : bookletLabels[MASTER_BOOKLET_CODE] ?? null

        Object.values(questionBookletOrders).forEach(items => {
          items.sort((a, b) => {
            if (masterLabel) {
              if (a.label === masterLabel && b.label !== masterLabel) {
                return -1
              }
              if (b.label === masterLabel && a.label !== masterLabel) {
                return 1
              }
            }
            return a.label.localeCompare(b.label)
          })
        })

        return (
          <div className='card mb-5' key={exam.examId}>
            <div className='card-header'>
              <div className='card-title d-flex flex-column flex-md-row justify-content-between w-100 gap-3'>
                <div>
                  <h4 className='mb-1'>{exam.courseCode} - {exam.examName}</h4>
                  <div className='text-muted'>{exam.semesterName} · {exam.examType}</div>
                </div>
                <div className='d-flex flex-wrap gap-3 align-items-center'>
                  {Object.values(OpticalStudentEntryStatus).map(status => (
                    <span key={status} className={`badge ${statusBadgeClass[status]} fw-semibold`}> 
                      {statusLabels[status]}: {statusCounts[status] ?? 0}
                    </span>
                  ))}
                  <span className='badge badge-light'>{`Eksik öğrenci: ${exam.missingStudents.length}`}</span>
                </div>
              </div>
            </div>
            <div className='card-body'>
              <div className='d-flex justify-content-end mb-4'>
                <button
                  type='button'
                  className='btn btn-light-primary btn-sm'
                  onClick={() => openBookletMappingModal(exam)}
                >
                  Kitapçık Eşleştirmelerini Yönet
                </button>
              </div>
              {summary && (
                <div className='mb-5'>
                  <h5 className='mb-3'>Sınav Özeti</h5>
                  <div className='table-responsive'>
                    <table className='table table-sm table-bordered mb-4'>
                      <tbody>
                        <tr>
                          <th scope='row'>Toplam Kayıt</th>
                          <td>{summary.totalEntries}</td>
                          <th scope='row'>Eşleşen</th>
                          <td>{summary.matchedEntries}</td>
                          <th scope='row'>Eksik Öğrenci</th>
                          <td>{exam.missingStudents.length}</td>
                        </tr>
                        <tr>
                          <th scope='row'>Mükerrer</th>
                          <td>{summary.duplicateEntries}</td>
                          <th scope='row'>Kayıtsız</th>
                          <td>{summary.unregisteredEntries}</td>
                          <th scope='row'>Kitapçık Hatası</th>
                          <td>{summary.bookletMismatchEntries}</td>
                        </tr>
                        <tr>
                          <th scope='row'>Birden Fazla Ders</th>
                          <td>{summary.ambiguousEntries}</td>
                          <th scope='row'>Yoksayılan</th>
                          <td>{summary.skippedEntries}</td>
                          <th scope='row'>Geçersiz</th>
                          <td>{summary.invalidEntries}</td>
                        </tr>
                        <tr>
                          <th scope='row'>Ortalama</th>
                          <td>{summary.averageScore != null ? summary.averageScore : '-'}</td>
                          <th scope='row'>Medyan</th>
                          <td>{summary.medianScore != null ? summary.medianScore : '-'}</td>
                          <th scope='row'>Std. Sapma</th>
                          <td>{summary.standardDeviation != null ? summary.standardDeviation : '-'}</td>
                        </tr>
                        <tr>
                          <th scope='row'>
                            <div className='d-inline-flex align-items-center gap-2'>
                              <span>α (0/1)</span>
                              <Tippy content={CRONBACH_ALPHA_TOOLTIP} maxWidth={320} placement='top'>
                                <span
                                  className='text-primary d-inline-flex align-items-center justify-content-center'
                                  style={{cursor: 'pointer'}}
                                >
                                  <i className='bi bi-question-circle-fill fs-5'></i>
                                </span>
                              </Tippy>
                            </div>
                          </th>
                          <td colSpan={5}>{summary.cronbachAlpha01 != null ? summary.cronbachAlpha01 : summary.cronbachAlpha != null ? summary.cronbachAlpha : '-'}</td>
                        </tr>
                        <tr>
                          <th scope='row'>
                            <div className='d-inline-flex align-items-center gap-2'>
                              <span>KR-20</span>
                              <Tippy content={KR20_TOOLTIP} maxWidth={320} placement='top'>
                                <span
                                  className='text-primary d-inline-flex align-items-center justify-content-center'
                                  style={{cursor: 'pointer'}}
                                >
                                  <i className='bi bi-question-circle-fill fs-5'></i>
                                </span>
                              </Tippy>
                            </div>
                          </th>
                          <td colSpan={5}>{summary.kr20 != null ? summary.kr20 : '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {cronbachComment && (
                    <div className='alert alert-light-primary py-2 px-3 mb-4'>
                      <div className='fw-semibold mb-1'>Cronbach Alpha Yorumu</div>
                      <div className='mb-0'>{cronbachComment}</div>
                    </div>
                  )}

                  {hasLearningOutcomeReliability && (
                    <div className='mb-4'>
                      <h6 className='mb-2'>Öğrenme Çıktısı Bazında Cronbach Alpha</h6>
                      <div className='table-responsive'>
                        <table className='table table-sm table-bordered mb-0'>
                          <thead>
                            <tr>
                              <th>Tür</th>
                              <th>Öğrenme Çıktısı</th>
                              <th>Soru Sayısı</th>
                              <th>α (0/1)</th>
                              <th>KR-20</th>
                              <th>Yorum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {learningOutcomeReliabilities.map(rel => {
                              const comment =
                                rel.questionCount <= 1
                                  ? 'Güvenirlik hesaplanabilmesi için en az 2 soru gerekir.'
                                  : rel.cronbachAlpha != null
                                  ? getCronbachAlphaComment(rel.cronbachAlpha01 ?? rel.cronbachAlpha, rel.questionCount)
                                  : null
                              const outcomeName =
                                rel.outcomeName && rel.outcomeName.trim().length > 0 ? rel.outcomeName : '-'

                              return (
                                <tr key={`${rel.type}-${rel.outcomeId}`}>
                                  <td>{getLearningOutcomeTypeLabel(rel.type)}</td>
                                  <td>{outcomeName}</td>
                                  <td>{rel.questionCount}</td>
                                  <td>
                                    {rel.cronbachAlpha01 != null
                                      ? rel.cronbachAlpha01
                                      : rel.cronbachAlpha != null
                                      ? rel.cronbachAlpha
                                      : '-'}
                                  </td>
                                  <td>{rel.kr20 != null ? rel.kr20 : '-'}</td>
                                  <td className='text-muted small'>{comment ?? '-'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {summary.questionAnalyses.length > 0 && (
                    <div className='table-responsive optical-responsive-table-container'>
                      <table className='table table-sm table-striped table-bordered mb-0 optical-responsive-table'>
                        <thead>
                          <tr>
                            <th>Soru</th>
                            <th>Kitapçık Sırası</th>
                            <th>Yanıtlayan</th>
                            <th>Doğru</th>
                            <th>Boş</th>
                            <th>Güçlük</th>
                            <th>Ayrırtedicilik</th>
                            <th>r (item-rest)</th>
                            <th>α (çıkarılınca)</th>
                            <th>Δα</th>
                            <th>Doğru Yanıt</th>
                            <th>İptal</th>
                            <th>YZ İncelemesi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.questionAnalyses.map(analysis => {
                            const mappingEntries = questionBookletOrders[analysis.examQuestionId] ?? []
                            const mappingLabel =
                              mappingEntries.length > 0
                                ? mappingEntries
                                    .map(entry => `${entry.label}: ${entry.questionNumber ?? '-'}`)
                                    .join(', ')
                                : '-'
                            const difficultyInsight = resolveDifficultyInsight(analysis)
                            const discriminationInsight = resolveDiscriminationInsight(analysis)
                            const manualAiReviewSetting = analysis.requiresAiReview
                            const requiresAiReview =
                              manualAiReviewSetting === true
                                ? true
                                : manualAiReviewSetting === false
                                ? false
                                : difficultyInsight.needsReview || discriminationInsight.needsReview
                            const {cacheKey, signature} = buildQuestionReviewData({
                              examId: exam.examId,
                              analysis,
                              difficultyInsight,
                              discriminationInsight,
                              questionAssignments: questionAssignmentsRef.current,
                              optionMappings: optionMappingsRef.current,
                              examConfigurations: examConfigurationsRef.current,
                            })
                            const cachedEntry = questionReviewCache[cacheKey]
                            const hasCachedComment =
                              cachedEntry?.signature === signature && Boolean(cachedEntry.comment)
                            const cachedCommentHtml = hasCachedComment
                              ? cachedEntry.formattedHtml ?? formatAiCommentHtml(cachedEntry.comment ?? '')
                              : null
                            const cachedError =
                              cachedEntry?.signature === signature && !cachedEntry?.comment && cachedEntry?.error
                                ? cachedEntry.error
                                : null
                            const isVeryEasyQuestion =
                              baseDifficultyInsight(analysis.difficulty).label.startsWith('Çok kolay')
                            const hasCachedData = hasCachedComment || Boolean(cachedError)
                            const assignmentReady = Boolean(
                              questionAssignments[exam.examId]?.[analysis.examQuestionId]
                            )
                            const difficultyValue =
                              typeof analysis.difficulty === 'number' ? analysis.difficulty : null
                            const discriminationValue =
                              typeof analysis.discrimination === 'number' ? analysis.discrimination : null
                            const isHardDifficulty =
                              difficultyValue !== null && difficultyValue < 0.4
                            const isVeryHardDifficulty =
                              difficultyValue !== null && difficultyValue < 0.2
                            const hasReverseDiscrimination =
                              discriminationValue !== null && discriminationValue < 0
                            const hasVeryLowDiscrimination =
                              discriminationValue !== null && discriminationValue < 0.2
                            const thresholdsRequireReview =
                              (hasReverseDiscrimination && isHardDifficulty) ||
                              ((hasReverseDiscrimination || hasVeryLowDiscrimination) &&
                                isVeryHardDifficulty)
                            const eligibleForCommentRequest =
                              requiresAiReview ||
                              (manualAiReviewSetting !== false && thresholdsRequireReview)
                            const canRequestComment =
                              isAiActive && !isVeryEasyQuestion && eligibleForCommentRequest
                            const showAiSection =
                              isAiActive && (canRequestComment || hasCachedData)
                            const requestDisabled = !assignmentReady && !hasCachedComment
                            const questionConfig = config.questions[analysis.examQuestionId]
                            const baseQuestion = questionLookup[analysis.examQuestionId]
                            const acceptedOptionsForDisplay = questionConfig
                              ? normalizeAcceptedOptionsList(questionConfig.defaultAcceptedOptions)
                              : baseQuestion && Array.isArray(baseQuestion.acceptedOptions)
                                ? normalizeAcceptedOptionsList(baseQuestion.acceptedOptions)
                                : []
                            const cachedAcceptedValue =
                              acceptedOptionsInputCache[exam.examId]?.[analysis.examQuestionId]
                            const acceptedValue =
                              typeof cachedAcceptedValue === 'string'
                                ? cachedAcceptedValue
                                : acceptedOptionsForDisplay.join(', ')

                            return (
                              <tr key={analysis.examQuestionId}>
                                <td data-label='Soru'>
                                  <div className='d-flex align-items-center gap-2 optical-responsive-table__number'>
                                    <button
                                      type='button'
                                      className='btn btn-link btn-sm p-0'
                                      onClick={() =>
                                        openQuestionPreview(exam.examId, analysis.examQuestionId, 'summary')
                                      }
                                    >
                                      {analysis.questionNumber}
                                    </button>
                                    {hasCachedComment && (
                                      <span className='badge badge-light-info text-uppercase'>YZ</span>
                                    )}
                                  </div>
                                </td>
                                <td data-label='Kitapçık Sırası'>{mappingLabel}</td>
                                <td data-label='Yanıtlayan'>{analysis.respondentCount}</td>
                                <td data-label='Doğru'>{analysis.correctCount}</td>
                                <td data-label='Boş'>{analysis.blankCount}</td>
                                <td data-label='Güçlük'>
                                  <div className='optical-responsive-table__value-group'>
                                    <div>{analysis.difficulty != null ? analysis.difficulty : '-'}</div>
                                    <div className={getInsightClassName(difficultyInsight)}>
                                      {difficultyInsight.label}
                                    </div>
                                  </div>
                                </td>
                                <td data-label='Ayrırtedicilik'>
                                  <div className='optical-responsive-table__value-group'>
                                    <div>{analysis.discrimination != null ? analysis.discrimination : '-'}</div>
                                    <div className={getInsightClassName(discriminationInsight)}>
                                      {discriminationInsight.label}
                                    </div>
                                  </div>
                                </td>
                                <td data-label='r (item-rest)'>
                                  {analysis.itemRestCorrelation != null ? analysis.itemRestCorrelation : '-'}
                                </td>
                                <td data-label='α (çıkarılınca)'>
                                  {analysis.alphaIfDeleted != null ? analysis.alphaIfDeleted : '-'}
                                </td>
                                <td
                                  data-label='Δα'
                                  className={
                                    analysis.alphaDelta != null && analysis.alphaDelta > 0
                                      ? 'text-success fw-semibold'
                                      : 'text-muted'
                                  }
                                >
                                  {analysis.alphaDelta != null
                                    ? analysis.alphaDelta > 0
                                      ? `+${analysis.alphaDelta}`
                                      : `${analysis.alphaDelta}`
                                    : '-'}
                                  {analysis.alphaDelta != null && analysis.alphaDelta > 0.02 && (
                                    <span className='badge badge-light-danger ms-1'>Çıkarılırsa alpha artar</span>
                                  )}
                                </td>
                                <td data-label='Doğru Yanıt' style={{minWidth: '200px'}}>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={acceptedValue}
                                    onChange={event =>
                                      handleAcceptedOptionsChange(
                                        exam.examId,
                                        analysis.examQuestionId,
                                        event.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      clearAcceptedOptionsInputCache(
                                        exam.examId,
                                        analysis.examQuestionId
                                      )
                                    }
                                    placeholder='Örn: A, B'
                                  />
                                </td>
                                <td data-label='İptal' className='text-center'>
                                  <div className='form-check form-check-sm form-check-custom form-check-solid d-flex justify-content-center'>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={Boolean(questionConfig?.isCancelled)}
                                      onChange={event =>
                                        handleToggleCancellation(
                                          exam.examId,
                                          analysis.examQuestionId,
                                          event.target.checked
                                        )
                                      }
                                    />
                                  </div>
                                </td>
                                <td data-label='YZ İncelemesi'>
                                  {!isAiActive ? (
                                    <span className='text-muted'>Yapay zekâ pasif</span>
                                  ) : showAiSection ? (
                                    <div className='d-flex flex-column gap-2 align-items-start w-100 optical-responsive-table__ai-actions'>
                                      {canRequestComment && (
                                        <button
                                          type='button'
                                          className={`btn btn-sm ${hasCachedComment ? 'btn-light-info' : 'btn-outline-warning'}`}
                                          onClick={() =>
                                            showQuestionReview(
                                              exam.examId,
                                              analysis,
                                              difficultyInsight,
                                              discriminationInsight
                                            )
                                          }
                                          disabled={requestDisabled}
                                        >
                                          {hasCachedComment ? 'Yorumu Yenile' : 'Yorum İste'}
                                        </button>
                                      )}
                                      {hasCachedComment && cachedCommentHtml && (
                                        <div
                                          className='alert alert-light-info border border-info-subtle mb-0 py-2 px-3 w-100'
                                          style={AI_COMMENT_PREVIEW_STYLE}
                                          dangerouslySetInnerHTML={{__html: cachedCommentHtml}}
                                        ></div>
                                      )}
                                      {canRequestComment && !assignmentReady && !hasCachedComment && (
                                        <small className='text-muted'>Soru seçenekleri yükleniyor...</small>
                                      )}
                                      {cachedError && (
                                        <div
                                          className='alert alert-light-danger border border-danger-subtle mb-0 py-2 px-3 w-100'
                                          style={{fontSize: '0.8125rem', lineHeight: 1.4}}
                                        >
                                          {cachedError}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className='text-muted'>Analiz gerekmiyor</span>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className='mb-5'>
                <h5 className='mb-3'>Kitapçık Bilgisi</h5>
                <div className='d-flex flex-column flex-lg-row gap-4'>
                  <div className='flex-grow-1'>
                    {availableBookletOptions.length > 0 ? (
                      <div className='border rounded'>
                        {exam.booklets
                          .map(booklet => {
                            const normalizedCode = normalizeBookletCode(booklet.code)
                            if (!normalizedCode) {
                              return null
                            }
                            const isExpanded = normalizedCode === expandedBookletCode
                            const label = booklet.code?.trim() || normalizedCode
                            return (
                              <div key={`${exam.examId}-${normalizedCode}`} className='border-bottom'>
                                <button
                                  type='button'
                                  className={`w-100 text-start btn btn-light ${isExpanded ? '' : 'collapsed'}`}
                                  onClick={() => handleToggleBookletSection(exam.examId, normalizedCode)}
                                >
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <span>{label}</span>
                                    <span className='text-muted small'>{Array.isArray(booklet.questions) ? booklet.questions.length : 0} soru</span>
                                  </div>
                                </button>
                                {isExpanded && (
                                  <div className='px-3 pb-3'>
                                    <div className='table-responsive'>
                                      <table className='table table-sm table-striped mb-0'>
                                        <thead>
                                          <tr>
                                            <th>#</th>
                                            <th>Puan</th>
                                            <th>Seçenekler</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {Array.isArray(booklet.questions) && booklet.questions.length > 0 ? (
                                            booklet.questions.map(question => (
                                              <tr key={question.examQuestionId}>
                                                <td>{question.questionNumber}</td>
                                                <td>
                                                  {question.effectivePoint != null
                                                    ? question.effectivePoint
                                                    : question.point ?? '-'}
                                                </td>
                                                <td>{question.acceptedOptions.join(', ') || '-'}</td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr>
                                              <td colSpan={3} className='text-center text-muted'>Soru bulunamadı.</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })
                          .filter(Boolean)}
                      </div>
                    ) : (
                      <div className='text-muted'>Kitapçık bilgisi bulunamadı.</div>
                    )}

                    {bookletCountList.length > 0 && (
                      <div className='mt-4'>
                        <h6 className='mb-2'>Kitapçık Dağılımı</h6>
                        <ul className='list-group list-group-flush'>
                          {bookletCountList.map(item => (
                            <li key={`${exam.examId}-${item.label}`} className='list-group-item d-flex justify-content-between align-items-center px-0'>
                              <span>{item.label}</span>
                              <span className='badge bg-light text-dark'>{item.count}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className='mt-5'>
                      <h5 className='mb-3'>Ceza Ayarları</h5>
                      <div className='form-check form-switch mb-3'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id={`penalty-${exam.examId}`}
                          checked={Boolean(config.penalty.applyPenalty)}
                          onChange={event => handlePenaltyToggle(exam.examId, event.target.checked)}
                        />
                        <label className='form-check-label' htmlFor={`penalty-${exam.examId}`}>
                          Çoktan seçmeli sorularda yanlışlara ceza uygula
                        </label>
                      </div>
                      <div className='row g-3'>
                        <div className='col-6'>
                          <label className='form-label form-label-sm'>Kaç yanlışta ceza</label>
                          <input
                            type='number'
                            min='0'
                            className='form-control form-control-sm'
                            value={config.penalty.wrongCount}
                            onChange={event =>
                              handlePenaltyValueChange(exam.examId, 'wrongCount', event.target.value)
                            }
                          />
                        </div>
                        <div className='col-6'>
                          <label className='form-label form-label-sm'>Kaç doğru silinecek</label>
                          <input
                            type='number'
                            min='0'
                            className='form-control form-control-sm'
                            value={config.penalty.correctDeduction}
                            onChange={event =>
                              handlePenaltyValueChange(exam.examId, 'correctDeduction', event.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className='mt-4'>
                        <label className='form-label'>İtiraz Notu</label>
                        <textarea
                          className='form-control'
                          rows={4}
                          value={config.note}
                          onChange={event => handleNoteChange(exam.examId, event.target.value)}
                          placeholder='Öğrenci itirazlarını iletmek için notunuzu yazın.'
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='table-responsive mb-5'>
                <table className='table table-striped table-bordered align-middle'>
                  <thead>
                    <tr>
                      <th>Kaynak</th>
                      <th>Öğrenci</th>
                      <th>Kitapçık</th>
                      <th>Sonuç</th>
                      <th>Durum</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exam.entries.map(entry => {
                      const officialName = [entry.studentName, entry.studentSurname]
                        .filter(
                          (value): value is string => typeof value === 'string' && value.trim().length > 0
                        )
                        .join(' ')
                      const scannedName = [entry.originalStudentName, entry.originalStudentSurname]
                        .filter(
                          (value): value is string => typeof value === 'string' && value.trim().length > 0
                        )
                        .join(' ')

                      return (
                        <tr key={entry.entryId}>
                          <td>
                            <div className='fw-semibold'>{entry.sourceFileName}</div>
                            <div className='text-muted'>Satır: {entry.sourceLineNumber}</div>
                            {entry.classroomCode && (
                              <div className='text-muted'>Derslik: {entry.classroomCode}</div>
                          )}
                        </td>
                        <td>
                          <div className='mb-3'>
                            <label className='form-label form-label-sm mb-1'>Öğrenci No</label>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={entry.studentNumber}
                              onChange={event =>
                                handleStudentNumberChange(exam.examId, entry.entryId, event.target.value)
                              }
                            />
                            {entry.originalStudentNumber && entry.originalStudentNumber !== entry.studentNumber && (
                              <div className='text-muted small'>Okunan: {entry.originalStudentNumber}</div>
                            )}
                          </div>
                          {officialName && <div className='text-muted mb-1'>{officialName}</div>}
                          {scannedName && (
                            <div className='text-muted small'>Okunan İsim-Soyisim: {scannedName}</div>
                          )}
                          {entry.notes && <div className='text-danger small'>{entry.notes}</div>}
                        </td>
                        <td>
                          <div className='mb-3'>
                            <label className='form-label form-label-sm mb-1'>Kitapçık</label>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={entry.bookletCode || ''}
                              maxLength={1}
                              onChange={event =>
                                handleBookletUpdate(exam.examId, entry.entryId, event.target.value)
                              }
                            />
                          </div>
                          {entry.originalBookletCode && (
                            <div className='text-muted small'>Okunan: {entry.originalBookletCode}</div>
                          )}
                          {entry.examTypeCode && (
                            <div className='text-muted small'>
                              Sınav Türü: {examTypeLabels[entry.examTypeCode] ?? entry.examTypeCode}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className='d-flex flex-column gap-1'>
                            <span>Doğru: {entry.correctCount}</span>
                            <span>Yanlış: {entry.wrongCount}</span>
                            <span>Boş: {entry.blankCount}</span>
                            <span>Toplam Puan: {entry.totalPoint ?? '-'}</span>
                            {entry.answerDetails.length > 0 && (
                              <button
                                type='button'
                                className='btn btn-sm btn-light'
                                onClick={() => handleOpenEntryDetails(exam.examId, entry.entryId)}
                              >
                                Cevapları Gör
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${statusBadgeClass[entry.status]}`}>
                            {statusLabels[entry.status]}
                          </span>
                          {entry.status === OpticalStudentEntryStatus.Matched && !entry.isPrimaryMatch && (
                            <div className='text-muted small'>Birincil değil</div>
                          )}
                        </td>
                        <td>{renderEntryActions(exam, entry)}</td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div>
                <h5>Eksik Öğrenciler</h5>
                {exam.missingStudents.length === 0 ? (
                  <div className='text-muted'>Tüm öğrenciler için kayıt bulundu.</div>
                ) : (
                  <ul className='list-group list-group-flush'>
                    {exam.missingStudents.map(student => (
                      <li className='list-group-item' key={student.studentSemesterCourseId}>
                        <div className='fw-semibold'>{student.studentNumber}</div>
                        <div className='text-muted'>
                          {[student.name, student.surname].filter(Boolean).join(' ')}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )
      })}

      {bookletMappingModal && (() => {
        const codes = Object.keys(bookletMappingModal.orders)
        const activeCode =
          bookletMappingModal.activeCode && codes.includes(bookletMappingModal.activeCode)
            ? bookletMappingModal.activeCode
            : codes[0] ?? null
        const activeOrders = activeCode ? bookletMappingModal.orders[activeCode] ?? [] : []
        const selectedQuestionId =
          bookletMappingModal.selectedQuestionId && activeOrders.includes(bookletMappingModal.selectedQuestionId)
            ? bookletMappingModal.selectedQuestionId
            : activeOrders[0] ?? null
        const activeOptionOrders = activeCode ? bookletMappingModal.optionOrders[activeCode] ?? {} : {}

        return (
          <>
            <div className='modal fade show d-block' role='dialog' aria-modal='true'>
              <div className='modal-dialog modal-xl modal-dialog-scrollable'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <div>
                      <h5 className='modal-title'>Kitapçık Eşleştirmeleri</h5>
                      <div className='text-muted small'>
                        {bookletMappingModal.exam.courseCode} · {bookletMappingModal.exam.examName}
                      </div>
                    </div>
                    <button
                      type='button'
                      className='btn-close'
                      aria-label='Kapat'
                      onClick={closeBookletMappingModal}
                    ></button>
                  </div>
                  <div className='modal-body'>
                    <div className='alert alert-light-primary border-primary mb-4'>
                      <div className='fw-semibold mb-1'>Kurtarma Seçenekleri</div>
                      <div className='d-flex flex-column flex-lg-row align-items-lg-center gap-3'>
                        <input
                          type='file'
                          accept='.xlsx'
                          className='form-control'
                          onChange={handleMappingFileChange}
                        />
                        <button
                          type='button'
                          className='btn btn-light-primary'
                          disabled={bookletMappingModal.isImporting}
                          onClick={handleImportBookletMapping}
                        >
                          {bookletMappingModal.isImporting ? 'Yükleniyor...' : 'Excelden Geri Yükle'}
                        </button>
                      </div>
                      <div className='text-muted small mt-2'>
                        Sınav öncesi indirilen cevap anahtarı excel dosyasını yükleyerek orijinal kitapçık sıralamasını
                        geri getirebilirsiniz.
                      </div>
                    </div>

                    <div className='mb-3 d-flex flex-wrap gap-2'>
                      {codes.length === 0 && <span className='text-muted'>Kitapçık bulunamadı.</span>}
                      {codes.map(code => (
                        <button
                          type='button'
                          key={code}
                          className={`btn btn-sm ${code === activeCode ? 'btn-primary' : 'btn-light-primary'}`}
                          onClick={() =>
                            setBookletMappingModal(prev =>
                              prev
                                ? {
                                    ...prev,
                                    activeCode: code,
                                    selectedQuestionId: prev.orders[code]?.[0] ?? prev.selectedQuestionId,
                                  }
                                : prev
                            )
                          }
                        >
                          {code}
                        </button>
                      ))}
                    </div>

                    <div className='border rounded p-3 bg-light'>
                      {activeCode ? (
                        <>
                          <div className='d-flex justify-content-between align-items-center mb-3'>
                            <div className='fw-semibold'>Kitapçık {activeCode}</div>
                            <div className='text-muted small'>
                              Sıralamayı sürükle-bırak veya oklar ile güncelleyebilirsiniz.
                            </div>
                          </div>
                          {activeOrders.length > 0 ? (
                            <ul className='list-group list-group-flush'>
                              {activeOrders.map((questionId, index) => (
                                <li
                                  key={`${activeCode}-${questionId}-${index}`}
                                  className={`list-group-item d-flex justify-content-between align-items-center ${
                                    questionId === bookletMappingModal.selectedQuestionId ? 'active' : ''
                                  }`}
                                  draggable
                                  onDragStart={() => handleBookletDragStart(activeCode, index)}
                                  onDragOver={event => event.preventDefault()}
                                  onDrop={() => handleBookletDrop(activeCode, index)}
                                  onClick={() =>
                                    setBookletMappingModal(prev =>
                                      prev ? {...prev, selectedQuestionId: questionId} : prev
                                    )
                                  }
                                  role='button'
                                >
                                  <div className='d-flex align-items-center gap-3'>
                                    <span className='badge badge-light-primary'>{index + 1}</span>
                                    <span
                                      className='fw-semibold'
                                      dangerouslySetInnerHTML={{
                                        __html: resolveQuestionLabel(
                                          bookletMappingModal.exam.examId,
                                          questionId
                                        ),
                                      }}
                                    ></span>
                                  </div>
                                  <div className='btn-group btn-group-sm'>
                                    <button
                                      type='button'
                                      className='btn btn-light'
                                      disabled={index === 0}
                                      onClick={() => moveBookletQuestion(activeCode, index, index - 1)}
                                    >
                                      ↑
                                    </button>
                                    <button
                                      type='button'
                                      className='btn btn-light'
                                      disabled={index === activeOrders.length - 1}
                                      onClick={() => moveBookletQuestion(activeCode, index, index + 1)}
                                    >
                                      ↓
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className='text-muted'>Soru listesi bulunamadı.</div>
                          )}

                          <div className='mt-4'>
                            <div className='d-flex justify-content-between align-items-center mb-2'>
                              <div className='fw-semibold'>Seçenek Sırası</div>
                              {selectedQuestionId && (
                                <span className='text-muted small'>
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: resolveQuestionLabel(
                                        bookletMappingModal.exam.examId,
                                        selectedQuestionId
                                      ),
                                    }}
                                  ></span>{' '}
                                  için seçenekleri düzenleyin.
                                </span>
                              )}
                            </div>

                            {selectedQuestionId ? (
                              (() => {
                                const assignment =
                                  questionAssignmentsRef.current[bookletMappingModal.exam.examId]?.[selectedQuestionId]
                                const optionOrderList = activeOptionOrders[selectedQuestionId] ?? []

                                const optionsById = new Map(
                                  (assignment?.options ?? [])
                                    .map(option =>
                                      typeof option?.id === 'number' ? ([option.id as number, option] as const) : null
                                    )
                                    .filter(Boolean) as Array<[number, ExamQuestionAssignment['options'][number]]>
                                )

                                const orderedOptions: ExamQuestionAssignment['options'] = []
                                optionOrderList.forEach(optionId => {
                                  const option = optionsById.get(optionId)
                                  if (option) {
                                    orderedOptions.push(option)
                                    optionsById.delete(optionId)
                                  }
                                })

                                optionsById.forEach(option => orderedOptions.push(option))

                                return orderedOptions.length > 0 ? (
                                  <ul className='list-group list-group-flush'>
                                    {orderedOptions.map((option, optionIndex) => (
                                      <li
                                        key={`${selectedQuestionId}-${option?.id ?? optionIndex}`}
                                        className='list-group-item d-flex justify-content-between align-items-center'
                                      >
                                        <div>
                                          <div
                                            className='fw-semibold'
                                            dangerouslySetInnerHTML={{
                                              __html: `${getLetterForIndex(optionIndex)}. ${
                                                option?.name ?? option?.key ?? 'Seçenek'
                                              }`,
                                            }}
                                          ></div>
                                          {option?.key && <div className='text-muted small'>{option.key}</div>}
                                        </div>
                                        <div className='d-flex align-items-center gap-2'>
                                          {option?.isTrue && (
                                            <span className='badge badge-light-success'>Doğru seçenek</span>
                                          )}
                                          <div className='btn-group btn-group-sm'>
                                            <button
                                              type='button'
                                              className='btn btn-light'
                                              disabled={optionIndex === 0}
                                              onClick={() =>
                                                moveBookletOption(
                                                  activeCode ?? MASTER_BOOKLET_CODE,
                                                  selectedQuestionId,
                                                  optionIndex,
                                                  optionIndex - 1
                                                )
                                              }
                                            >
                                              ↑
                                            </button>
                                            <button
                                              type='button'
                                              className='btn btn-light'
                                              disabled={optionIndex === orderedOptions.length - 1}
                                              onClick={() =>
                                                moveBookletOption(
                                                  activeCode ?? MASTER_BOOKLET_CODE,
                                                  selectedQuestionId,
                                                  optionIndex,
                                                  optionIndex + 1
                                                )
                                              }
                                            >
                                              ↓
                                            </button>
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className='text-muted'>Seçenek listesi bulunamadı.</div>
                                )
                              })()
                            ) : (
                              <div className='text-muted'>Seçenek düzenlemek için bir soru seçin.</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className='text-muted'>Görüntülenecek kitapçık bulunamadı.</div>
                      )}
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <div className='text-muted small me-auto'>
                      Değişiklikler kaydedildiğinde optik okuma sırasında doğru eşleştirme kullanılacaktır.
                    </div>
                    <button type='button' className='btn btn-light' onClick={closeBookletMappingModal}>
                      Kapat
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary'
                      disabled={bookletMappingModal.isSaving}
                      onClick={handleSaveBookletMapping}
                    >
                      {bookletMappingModal.isSaving ? 'Kaydediliyor...' : 'Sıralamayı Kaydet'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-backdrop fade show'></div>
          </>
        )
      })()}

      {questionPreviewContext && (() => {
        const {examId, examQuestionId, bookletCode: previewBookletCode} = questionPreviewContext
        const exam = examResults.find(item => item.examId === examId)
        const assignment = (questionAssignments[examId] ?? {})[examQuestionId]
        const mapping = optionMappings[examId]?.[examQuestionId]
        const config = examConfigurations[examId] ?? (exam ? createInitialConfiguration(exam) : undefined)
        const questionConfig = config?.questions[examQuestionId]
        const examQuestion = exam?.questions.find(question => question.examQuestionId === examQuestionId)
        const orderMapForExam = bookletQuestionOrders[examId] ?? {}
        const normalizedMasterCode = normalizeBookletCode(exam?.masterBookletCode)
        const masterOrderMap =
          (normalizedMasterCode && orderMapForExam[normalizedMasterCode]) ??
          orderMapForExam[MASTER_BOOKLET_CODE] ??
          null
        const questionNumber =
          masterOrderMap?.[examQuestionId] ?? examQuestion?.questionNumber ?? (assignment?.order ?? null)
        const questionHtml = enhanceQuestionHtml(assignment?.questionName ?? '')

        const optionEntries: ExamQuestionAssignment['options'] = assignment?.options ?? []
        const baseMapping = mapping?.[BASE_OPTION_MAPPING_KEY]
        const optionDescriptions = optionEntries.map((option, index) => {
          const optionId = typeof option.id === 'number' && Number.isFinite(option.id) ? (option.id as number) : null
          const mappedLetter = optionId !== null ? mapOptionIdsToLetters([optionId], baseMapping)[0] : undefined
          const resolvedLetter = mappedLetter ?? option.key ?? ''
          const normalizedLetter = resolvedLetter ? resolvedLetter.toString().trim().toUpperCase() : String.fromCharCode(65 + index)
          const html = enhanceQuestionHtml(option.name ?? '')

          return {
            key: optionId !== null ? `option-${optionId}` : `option-${index}`,
            letter: normalizedLetter,
            html,
            isCorrect: Boolean(option.isTrue),
          }
        })

        const correctOptionIds = optionEntries
          .filter(option => option.isTrue && typeof option.id === 'number' && Number.isFinite(option.id))
          .map(option => option.id as number)

        const examBookletLookup = Array.isArray(exam?.booklets)
          ? exam!.booklets.reduce<
              Record<string, {label: string; questions: Record<number, OpticalFormBookletQuestion>}>
            >(
              (acc, booklet) => {
                const normalized = normalizeBookletCode(booklet.code)
                if (!normalized) {
                  return acc
                }

                const questions = Array.isArray(booklet.questions)
                  ? booklet.questions.reduce<Record<number, OpticalFormBookletQuestion>>((map, bookletQuestion) => {
                      map[bookletQuestion.examQuestionId] = bookletQuestion
                      return map
                    }, {})
                  : {}

                acc[normalized] = {
                  label: booklet.code?.trim() || normalized,
                  questions,
                }
                return acc
              },
              {}
            )
          : {}

        const codesToInclude = new Set<string>()
        Object.keys(orderMapForExam).forEach(code => {
          if (code === MASTER_BOOKLET_CODE && normalizedMasterCode) {
            codesToInclude.add(normalizedMasterCode)
          } else {
            codesToInclude.add(code)
          }
        })
        Object.keys(examBookletLookup).forEach(code => {
          codesToInclude.add(code)
        })
        if (normalizedMasterCode) {
          codesToInclude.add(normalizedMasterCode)
        }

        const examBooklets = Array.from(codesToInclude)
          .map(code => {
            if (!code) {
              return null
            }
            const bookletInfo = examBookletLookup[code]
            const label = (() => {
              if (bookletInfo) {
                return bookletInfo.label
              }
              if (code === MASTER_BOOKLET_CODE) {
                return 'Master Kitapçık'
              }
              return code
            })()
            const orderMapForCode =
              orderMapForExam[code] ??
              (code === normalizedMasterCode ? orderMapForExam[MASTER_BOOKLET_CODE] : undefined)
            const fallbackQuestion = bookletInfo?.questions?.[examQuestionId]
            const questionNumberInBooklet =
              orderMapForCode?.[examQuestionId] ??
              fallbackQuestion?.questionNumber ??
              (code === normalizedMasterCode ? questionNumber ?? null : null)

            const defaultAccepted = (() => {
              const fallbackAccepted = fallbackQuestion?.acceptedOptions ?? []
              if (Array.isArray(fallbackAccepted) && fallbackAccepted.length > 0) {
                return [...fallbackAccepted]
              }
              const directConfig = questionConfig?.acceptedOptionsByBooklet?.[code]
              if (Array.isArray(directConfig) && directConfig.length > 0) {
                return [...directConfig]
              }
              if (code === normalizedMasterCode) {
                const masterAccepted = questionConfig?.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE]
                if (Array.isArray(masterAccepted) && masterAccepted.length > 0) {
                  return [...masterAccepted]
                }
                const defaultAcceptedOptions = questionConfig?.defaultAcceptedOptions
                return Array.isArray(defaultAcceptedOptions) ? [...defaultAcceptedOptions] : []
              }
              return []
            })()

            return {
              code,
              label,
              defaultAccepted,
              questionNumber: typeof questionNumberInBooklet === 'number' ? questionNumberInBooklet : null,
            }
          })
          .filter(
            (
              item
            ): item is {code: string; label: string; defaultAccepted: string[]; questionNumber: number | null} =>
              Boolean(item && item.code)
          )

        examBooklets.sort((a, b) => {
          if (normalizedMasterCode) {
            if (a.code === normalizedMasterCode && b.code !== normalizedMasterCode) {
              return -1
            }
            if (b.code === normalizedMasterCode && a.code !== normalizedMasterCode) {
              return 1
            }
          }
          return a.label.localeCompare(b.label)
        })

        const masterMapping =
          mapping?.[MASTER_BOOKLET_CODE] ??
          (normalizedMasterCode ? mapping?.[normalizedMasterCode] : undefined) ??
          baseMapping

        const bookletRows = examBooklets.map(booklet => {
          const configuredAccepted =
            questionConfig?.acceptedOptionsByBooklet[booklet.code] ??
            (normalizedMasterCode && booklet.code === normalizedMasterCode
              ? questionConfig?.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE]
              : undefined) ??
            booklet.defaultAccepted
          const normalizedAccepted = normalizeAcceptedOptionsList(configuredAccepted)
          const mappingForCode =
            mapping?.[booklet.code] ??
            (normalizedMasterCode && booklet.code === normalizedMasterCode
              ? mapping?.[MASTER_BOOKLET_CODE]
              : undefined) ??
            baseMapping
          const bankLetters =
            correctOptionIds.length > 0 ? mapOptionIdsToLetters(correctOptionIds, masterMapping) : []

          return {
            code: booklet.code,
            label: booklet.label,
            bankLetters,
            accepted: normalizedAccepted,
            questionNumber: booklet.questionNumber,
          }
        })

        const highlightedCode = previewBookletCode
        const modalTitle = questionNumber != null ? `Soru ${questionNumber} Detayı` : 'Soru Detayı'

        return (
          <>
            <div
              className='modal fade show d-block'
              role='dialog'
              aria-modal='true'
              style={{zIndex: 1060}}
            >
              <div className='modal-dialog modal-lg modal-dialog-scrollable'>
                <div
                    className='modal-content shadow-lg'
                    style={{ border: '2px solid rgba(213, 110, 53, 0.45)', boxShadow: '0 0 2rem rgba(0, 0, 0, 0.45)' }}
                >
                  <div className='modal-header'>
                    <h5 className='modal-title'>{modalTitle}</h5>
                    <button type='button' className='btn-close' aria-label='Kapat' onClick={closeQuestionPreview}></button>
                  </div>
                  <div className='modal-body'>
                    {assignment ? (
                      <>
                        <div className='mb-4'>
                          <div className='fw-semibold mb-2'>Soru</div>
                          <div className='border rounded p-3 bg-light'>
                            <div className='image-wrapper' dangerouslySetInnerHTML={{__html: questionHtml}}></div>
                          </div>
                        </div>
                        <div className='mb-4'>
                          {optionDescriptions.length > 0 ? (
                            <ul className='mb-0 ps-0 list-unstyled d-flex flex-column gap-2'>
                              {optionDescriptions.map(option => (
                                <li key={option.key} className='d-flex gap-3 align-items-start'>
                                  <span className={`text-uppercase ${option.isCorrect ? 'fw-semibold' : ''}`}>
                                    {option.letter || '-'}
                                  </span>
                                  <div
                                    className={`${option.isCorrect ? 'fw-semibold ' : ''}image-wrapper`}
                                    dangerouslySetInnerHTML={{__html: option.html || '-'}}
                                  ></div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className='text-muted'>Seçenek bilgisi bulunamadı.</div>
                          )}
                        </div>
                        <div className='table-responsive'>
                          <table className='table table-sm table-striped mb-0'>
                            <thead>
                              <tr>
                                <th>Kitapçık</th>
                                <th>Soru No</th>
                                <th>Soru Bankası</th>
                                <th>Optik Ayarı</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bookletRows.length > 0 ? (
                                bookletRows.map(row => (
                                  <tr
                                    key={`${examId}-${row.code}`}
                                    className={highlightedCode && highlightedCode === row.code ? 'table-active' : ''}
                                  >
                                    <td>{row.label}</td>
                                    <td>{row.questionNumber ?? '-'}</td>
                                    <td>{row.bankLetters.join(', ') || '-'}</td>
                                    <td>{row.accepted.join(', ') || '-'}</td>
                                  </tr>
                                ))
                                ) : (
                                  <tr>
                                    <td colSpan={4} className='text-center text-muted'>Kitapçık bilgisi bulunamadı.</td>
                                  </tr>
                                )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className='text-muted'>Soru detayları yüklenemedi.</div>
                    )}
                  </div>
                  <div className='modal-footer'>
                    <button type='button' className='btn btn-light' onClick={closeQuestionPreview}>
                      Kapat
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-backdrop fade show' style={{zIndex: 1055}}></div>
          </>
        )
      })()}

      {detailModalContext && (() => {
        const officialName = [detailModalContext.entry.studentName, detailModalContext.entry.studentSurname]
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          .join(' ')
        const scannedName = [
          detailModalContext.entry.originalStudentName,
          detailModalContext.entry.originalStudentSurname,
        ]
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          .join(' ')

        return (
          <>
            <div className='modal fade show d-block' role='dialog' aria-modal='true'>
              <div className='modal-dialog modal-xl modal-dialog-scrollable'>
                <div
                  className='modal-content shadow-lg'
                  style={{border: '2px solid rgba(13, 110, 253, 0.35)', boxShadow: '0 0 2rem rgba(0, 0, 0, 0.35)'}}
                >
                  <div className='modal-header'>
                    <h5 className='modal-title'>Cevap Detayları - {detailModalContext.entry.studentNumber}</h5>
                    <button type='button' className='btn-close' aria-label='Kapat' onClick={closeDetailModal}></button>
                  </div>
                  <div className='modal-body'>
                    <div className='mb-4'>
                      <div><strong>Öğrenci:</strong> {detailModalContext.entry.studentNumber}</div>
                      <div>
                        <strong>Adı Soyadı:</strong> {officialName || '-'}
                      </div>
                      {scannedName && (
                        <div>
                          <strong>Okunan Adı Soyadı:</strong> {scannedName}
                        </div>
                      )}
                      <div><strong>Kitapçık:</strong> {detailModalContext.entry.bookletCode || '-'}</div>
                      <div><strong>Toplam Puan:</strong> {detailModalContext.entry.totalPoint ?? '-'}</div>
                    </div>
                  <div className='table-responsive'>
                    <table className='table table-sm table-striped align-middle mb-0'>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>İşaretlenen</th>
                          <th>Kabul Edilen</th>
                          <th>Durum</th>
                          <th>Puan</th>
                          <th>Önizleme</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const detailBookletOrders =
                            bookletQuestionOrders[detailModalContext.exam.examId] ?? {}
                          const normalizedDetailMaster = normalizeBookletCode(
                            detailModalContext.exam.masterBookletCode
                          )
                          const normalizedEntryBooklet = normalizeBookletCode(
                            detailModalContext.entry.bookletCode
                          )

          const config =
            examConfigurations[detailModalContext.exam.examId] ??
            createInitialConfiguration(detailModalContext.exam)

          return detailModalContext.entry.answerDetails.map(detail => {
            const questionConfig = config.questions[detail.examQuestionId]
            const acceptedFromConfig = (() => {
              if (!questionConfig) {
                return null
              }

              const candidates: string[][] = []
              if (normalizedEntryBooklet) {
                const direct = normalizeAcceptedOptionsList(
                  questionConfig.acceptedOptionsByBooklet[normalizedEntryBooklet]
                )
                if (direct.length > 0) {
                  candidates.push(direct)
                }

                if (
                  normalizedDetailMaster &&
                  normalizedEntryBooklet === normalizedDetailMaster
                ) {
                  const masterNormalized = normalizeAcceptedOptionsList(
                    questionConfig.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE] ??
                      questionConfig.acceptedOptionsByBooklet[normalizedDetailMaster]
                  )
                  if (masterNormalized.length > 0) {
                    candidates.push(masterNormalized)
                  }
                }
              }

              if (normalizedDetailMaster) {
                const masterDirect = normalizeAcceptedOptionsList(
                  questionConfig.acceptedOptionsByBooklet[normalizedDetailMaster]
                )
                if (masterDirect.length > 0) {
                  candidates.push(masterDirect)
                }
              }

              const masterFallback = normalizeAcceptedOptionsList(
                questionConfig.acceptedOptionsByBooklet[MASTER_BOOKLET_CODE]
              )
              if (masterFallback.length > 0) {
                candidates.push(masterFallback)
              }

              const defaults = normalizeAcceptedOptionsList(
                questionConfig.defaultAcceptedOptions
              )
              if (defaults.length > 0) {
                candidates.push(defaults)
              }

              const selected = candidates.find(options => options.length > 0)
              return selected && selected.length > 0 ? selected.join(', ') : null
            })()

            const accepted = acceptedFromConfig ??
              (Array.isArray(detail.acceptedOptions) && detail.acceptedOptions.length > 0
                ? detail.acceptedOptions.join(', ')
                : detail.correctOption ?? '-')
                            const rowClass = detail.isCancelled
                              ? 'table-secondary'
                            : detail.isCorrect
                              ? 'table-success'
                              : detail.studentOption
                                ? 'table-danger'
                                : ''
                          const statusLabel = detail.isCancelled
                            ? 'İptal'
                            : detail.isCorrect
                              ? 'Doğru'
                              : detail.studentOption
                                ? 'Yanlış'
                                : 'Boş'

                          const orderMapForEntry =
                            normalizedEntryBooklet
                              ? detailBookletOrders[normalizedEntryBooklet] ??
                                (normalizedDetailMaster && normalizedEntryBooklet === normalizedDetailMaster
                                  ? detailBookletOrders[MASTER_BOOKLET_CODE]
                                  : undefined)
                              : undefined
                          const masterOrderForDetail =
                            normalizedDetailMaster
                              ? detailBookletOrders[normalizedDetailMaster] ??
                                detailBookletOrders[MASTER_BOOKLET_CODE]
                              : detailBookletOrders[MASTER_BOOKLET_CODE]
                          const resolvedQuestionNumber =
                            orderMapForEntry?.[detail.examQuestionId] ??
                            masterOrderForDetail?.[detail.examQuestionId] ??
                            detail.questionNumber

                          return (
                            <tr key={`${detailModalContext.entry.entryId}-${detail.questionNumber}`} className={rowClass}>
                              <td>{resolvedQuestionNumber ?? '-'}</td>
                              <td>{detail.studentOption ?? '-'}</td>
                              <td>{accepted}</td>
                              <td>{statusLabel}</td>
                              <td>{detail.point ?? 0}</td>
                              <td>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary'
                                  onClick={() =>
                                    openQuestionPreview(
                                      detailModalContext.exam.examId,
                                      detail.examQuestionId,
                                      'detail',
                                      detailModalContext.entry.bookletCode
                                    )
                                  }
                                >
                                  Görüntüle
                                </button>
                              </td>
                            </tr>
                          )
                          })
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-light' onClick={closeDetailModal}>
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
            <div className='modal-backdrop fade show'></div>
          </>
        )
      })()}
    </div>
  )
}

export default ExamOpticalReadingPage
